import React, { useState, Suspense, useRef, useEffect } from "react"
import { Canvas, extend, useFrame, useLoader, useThree } from "@react-three/fiber"
import { Environment, Html, useProgress } from "@react-three/drei"
import { useGLTF } from "../src/hooks/useGLTF"
import { EffectComposer, N8AO, SSAO, Bloom, Noise, ToneMapping, Vignette } from "@react-three/postprocessing"
import { useTranslation } from "react-i18next"
import { ToneMappingMode } from "postprocessing"
import { useReseller } from "../src/context/resellerContext"
import LanguageOverlay from "../src/components/languageOverlay"
import PrivacyOverlay from "../src/components/privacyOverlay"
import ResellerBar from "../src/components/resellerBar"
// import { InView, useInView } from "react-intersection-observer";
import { OrbitControls } from "../src/OrbitControls"
import { isMobile } from "react-device-detect"
import { RepeatWrapping, MeshPhysicalMaterial, DoubleSide, HalfFloatType, SRGBColorSpace } from "three"
import "../src/services/i18n"
import i18n from "i18next"
import { TextureLoader } from "three/src/loaders/TextureLoader"
import ModelConfig from "../src/modelConfig.json"
import { CircularProgressbar, buildStyles } from "react-circular-progressbar"
import "react-circular-progressbar/dist/styles.css"
import * as THREE from "three"

extend({ OrbitControls })

const normalizeHexColor = (hex) => {
  if (typeof hex !== "string") return hex
  return hex.startsWith("#") && hex.length === 9 ? hex.slice(0, 7) : hex
}

const findGen2MaterialColor = (config, colorIndex, materialName, materialColorOptions = config.materialColorOptions) => {
  const colorName = config.colorNames[colorIndex]
  return materialColorOptions?.[colorName]?.[materialName]
}

const GEN2_COLOR_TRANSITION_MS = 100
const SPICE_TRANSPARENT_COLOR_MATERIAL = "transparant"
const SPICE_TRANSPARENT_MATERIALS = ["transparant", "transperant", "transparantOuter", "transparantInner"]
const SABRINA_FROSTED_MATERIAL = "transflaps"
const MARIAH_YOLK_GRADIENT_COLOR = "Vanilla Cream & Ice Blue"
const MARIAH_YOLK_GRADIENT_TOP = "#EEEAD9"
const MARIAH_YOLK_GRADIENT_BOTTOM = "#C9DEE8"
const MARIAH_SCRIM_TEXTURE_COLOR = "Sky Pink"
const MARIAH_SCRIM_TEXTURE_MATERIAL = "scrim"
const AUBREY_NON_METALLIC_LOGO_COLORS = ["Olive Green", "Sky Pink"]
const AUBREY_SKY_PINK_NON_METALLIC_MATERIALS = ["logo", "budCapsTop", "plateButton"]
const QCE_DEWDROP_DARK_PARTS = ["Inner", "lidSmall", "tips"]
const QCE_DEWDROP_DARK_COLOR = { r: 0.04, g: 0.16, b: 0.16 }
const QCE_MAUVE_OVERRIDE_PARTS = ["Inner", "lidSmall"]
const QCE_MAUVE_OVERRIDE_COLOR = { r: 0.48, g: 0.1, b: 0.27 }

let mariahScrimTexture = null
let donnieWoodTexture = null

const getEaseOutProgress = (startTime) => {
  const progress = Math.min((performance.now() - startTime) / GEN2_COLOR_TRANSITION_MS, 1)
  return 1 - Math.pow(1 - progress, 2)
}

const getMariahYolkGradientTexture = (material) => {
  if (material.userData?.mariahYolkGradientTexture) return material.userData.mariahYolkGradientTexture

  const canvas = document.createElement("canvas")
  canvas.width = 16
  canvas.height = 256
  const context = canvas.getContext("2d")
  const gradient = context.createLinearGradient(0, 0, 0, canvas.height)
  gradient.addColorStop(0, MARIAH_YOLK_GRADIENT_TOP)
  gradient.addColorStop(1, MARIAH_YOLK_GRADIENT_BOTTOM)
  context.fillStyle = gradient
  context.fillRect(0, 0, canvas.width, canvas.height)

  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = SRGBColorSpace
  texture.flipY = false
  texture.wrapS = THREE.ClampToEdgeWrapping
  texture.wrapT = THREE.ClampToEdgeWrapping
  material.userData.mariahYolkGradientTexture = texture
  return texture
}

const getMariahScrimTexture = () => {
  if (mariahScrimTexture) return mariahScrimTexture

  mariahScrimTexture = new TextureLoader().load("/mariahScrim.webp")
  mariahScrimTexture.colorSpace = SRGBColorSpace
  mariahScrimTexture.flipY = false
  mariahScrimTexture.wrapS = RepeatWrapping
  mariahScrimTexture.wrapT = RepeatWrapping
  return mariahScrimTexture
}

const getDonnieWoodTexture = () => {
  if (donnieWoodTexture) return donnieWoodTexture

  donnieWoodTexture = new TextureLoader().load("/donnieWood.jpg")
  donnieWoodTexture.colorSpace = SRGBColorSpace
  donnieWoodTexture.flipY = false
  donnieWoodTexture.wrapS = RepeatWrapping
  donnieWoodTexture.wrapT = RepeatWrapping
  return donnieWoodTexture
}

const applyAubreyMetalnessOverrides = (material, colorName) => {
  if (material.name !== "logo" && !AUBREY_SKY_PINK_NON_METALLIC_MATERIALS.includes(material.name)) return

  if (typeof material.userData.aubreyOriginalMetalness !== "number") {
    material.userData.aubreyOriginalMetalness = material.metalness
  }

  const shouldDisableLogoMetalness = material.name === "logo" && AUBREY_NON_METALLIC_LOGO_COLORS.includes(colorName)
  const shouldDisableSkyPinkMaterialMetalness =
    colorName === "Sky Pink" && AUBREY_SKY_PINK_NON_METALLIC_MATERIALS.includes(material.name)

  material.metalness = shouldDisableLogoMetalness || shouldDisableSkyPinkMaterialMetalness ? 0 : material.userData.aubreyOriginalMetalness
}

const findSpiceMaterialColor = (config, colorIndex, materialName, materialColorOptions) => {
  const materialColor = findGen2MaterialColor(config, colorIndex, materialName, materialColorOptions)
  if (materialColor || !SPICE_TRANSPARENT_MATERIALS.includes(materialName)) return materialColor

  return findGen2MaterialColor(config, colorIndex, SPICE_TRANSPARENT_COLOR_MATERIAL, materialColorOptions)
}

const createSpiceTransparentMaterial = (material) => {
  const transparentMaterial = new THREE.MeshPhysicalMaterial({
    roughness: 0.31,
    transmission: 1,
    thickness: 0.04,
  })

  transparentMaterial.name = material.name
  transparentMaterial.color.copy(material.color)
  transparentMaterial.envMapIntensity = material.envMapIntensity
  transparentMaterial.dithering = material.dithering
  transparentMaterial.toneMapped = material.toneMapped
  transparentMaterial.userData.spiceTransparentMaterial = true
  return transparentMaterial
}

const createSabrinaFrostedMaterial = (material) => {
  const frostedMaterial = new THREE.MeshPhysicalMaterial({
    roughness: 0.55,
    transmission: 0.65,
    thickness: 0.5,
    transparent: true,
    opacity: 0.98,

  })

  frostedMaterial.name = material.name
  frostedMaterial.color.copy(material.color)
  frostedMaterial.envMapIntensity = material.envMapIntensity
  frostedMaterial.dithering = material.dithering
  frostedMaterial.toneMapped = material.toneMapped
  frostedMaterial.userData.sabrinaFrostedMaterial = true
  return frostedMaterial
}

const normalizeOpaqueMaterialState = (material) => {
  const isBodyMaterial = material.name?.toLowerCase().includes("body")
  const hasAlphaMap = Boolean(material.alphaMap)
  const hasTransparentTexture = Boolean(!isBodyMaterial && material.transparent && material.map)
  const usesTransmission = Boolean(material.transmission || material.userData?.spiceTransparentMaterial)
  const hasPartialOpacity = typeof material.opacity === "number" && material.opacity < 1

  if (hasAlphaMap || hasTransparentTexture || usesTransmission || hasPartialOpacity) return

  material.opacity = 1
  material.transparent = false
  material.depthWrite = true
  material.depthTest = true
}

const getGen2MaterialNames = (materialColorOptions = {}) => {
  return Array.from(
    new Set(
      Object.values(materialColorOptions).flatMap((materials) => {
        return Object.keys(materials || {})
      })
    )
  )
}

const cloneConfigValue = (value) => {
  return JSON.parse(JSON.stringify(value || {}))
}

const getColorInputValue = (value) => {
  const normalized = normalizeHexColor(value || "")
  return /^#[0-9a-fA-F]{6}$/.test(normalized) ? normalized : "#000000"
}

const getMaterialSnapshot = (material) => {
  return {
    color: `#${material.color.getHexString()}`,
    roughness: material.roughness,
    metalness: material.metalness,
    opacity: material.opacity,
    transparent: material.transparent,
    transmission: material.transmission,
    thickness: material.thickness,
    envMapIntensity: material.envMapIntensity,
  }
}

const getMaterialNames = (material) => {
  const materials = Array.isArray(material) ? material : [material]
  return materials.map((item) => item?.name || "unnamed").filter(Boolean)
}

const getConfigValue = (value, index, fallback) => {
  if (Array.isArray(value)) {
    return value[index] ?? value[value.length - 1] ?? fallback
  }
  return value ?? fallback
}

const getInitialRenderSettings = (config, colorIndex) => {
  return {
    backgroundColor: "#ffffff",
    antialias: false,
    alpha: false,
    flat: true,
    dprDesktop: 1,
    dprMobile: 2,
    environment: true,
    shadow: Boolean(config?.shadow),
    postProcessing: true,
    n8ao: true,
    toneMapping: true,
    bloom: false,
    noise: false,
    vignette: true,
    ambient: getConfigValue(config?.ambient, colorIndex, 1),
    envmap: getConfigValue(config?.envmap, colorIndex, 1),
    ssaoIntensity: getConfigValue(config?.ssaoIntensity, colorIndex, 1),
    aoRadius: 0.8,
    aoSamples: 25,
    denoiseSamples: 14,
    toneResolution: 256,
    toneMaxLuminance: 4,
    toneWhitePoint: 6,
    toneMiddleGrey: 0.2,
    toneAverageLuminance: 1,
    bloomIntensity: getConfigValue(config?.bloomValue, colorIndex, 0.895),
    bloomLuminanceThreshold: getConfigValue(config?.luminanceThreshold, colorIndex, config?.luminanceThreshold ?? 0.75),
    bloomLuminanceSmoothing: 0.2,
    noiseOpacity: 0.03,
    vignetteOffset: 0.35,
    vignetteDarkness: 0.5,
    hdri: config?.hdri || "",
  }
}

let expressiveLightIdCounter = 0

const getInitialExpressiveLighting = (index = 0) => ({
  id: `${Date.now()}-${expressiveLightIdCounter++}-${index}`,
  name: `Light ${index + 1}`,
  type: "spot",
  enabled: index !== 0,
  pinToCamera: false,
  rotate: index === 0 ? 173 : 78,
  width: index === 0 ? 9.9 : 13,
  y: index === 0 ? -1.2 : -0.9,
  color: index === 0 ? "#ff00ea" : "#ff0000",
  intensity: index === 0 ? 19.2 : 23.8,
  angle: index === 0 ? 0.53 : 0.45,
  penumbra: index === 0 ? 0.54 : 0.6,
  distance: 30,
  decay: 1,
  areaWidth: 4,
  areaHeight: 4,
})

const getExpressiveLightPosition = (settings, center, cameraOrbitRadians = 0, pinnedCameraAngle = 0) => {
  const cameraRotationDelta = settings.pinToCamera ? cameraOrbitRadians - pinnedCameraAngle : 0
  const rotateRadians = THREE.MathUtils.degToRad(settings.rotate) + cameraRotationDelta

  return [
    center[0] + Math.cos(rotateRadians) * settings.width,
    center[1] + settings.y,
    center[2] + Math.sin(rotateRadians) * settings.width,
  ]
}

const CanvasSettingsApplier = ({ backgroundColor }) => {
  const { gl } = useThree()

  useEffect(() => {
    gl.setClearColor(backgroundColor)
  }, [gl, backgroundColor])

  return null
}

const ExpressiveLight = ({ settings, targetPosition = [0, 0, 0] }) => {
  const targetRef = useRef(new THREE.Object3D())
  const lightRef = useRef()
  const areaLightRef = useRef()
  const pinStateRef = useRef({
    active: false,
    cameraAngle: 0,
  })
  const { camera, scene } = useThree()
  const center = Array.isArray(targetPosition) ? targetPosition : [0, 0, 0]

  useEffect(() => {
    const target = targetRef.current
    scene.add(target)
    return () => scene.remove(target)
  }, [scene])

  useEffect(() => {
    targetRef.current.position.set(center[0], center[1], center[2])
  }, [center[0], center[1], center[2]])

  useFrame(() => {
    const cameraOrbitRadians = Math.atan2(camera.position.z - center[2], camera.position.x - center[0])

    if (settings.pinToCamera && !pinStateRef.current.active) {
      pinStateRef.current = {
        active: true,
        cameraAngle: cameraOrbitRadians,
      }
    }

    if (!settings.pinToCamera && pinStateRef.current.active) {
      pinStateRef.current.active = false
    }

    const [x, y, z] = getExpressiveLightPosition(settings, center, cameraOrbitRadians, pinStateRef.current.cameraAngle)

    if (lightRef.current) {
      lightRef.current.position.set(x, y, z)
      if (settings.type === "spot") {
        lightRef.current.target = targetRef.current
        targetRef.current.updateMatrixWorld()
      }
    }

    if (areaLightRef.current) {
      areaLightRef.current.position.set(x, y, z)
      areaLightRef.current.lookAt(center[0], center[1], center[2])
    }
  })

  if (settings.type === "point") {
    return (
      <pointLight
        ref={lightRef}
        color={settings.color}
        intensity={settings.intensity}
        distance={settings.distance}
        decay={settings.decay}
        castShadow={false}
      />
    )
  }

  if (settings.type === "area") {
    return (
      <rectAreaLight
        ref={areaLightRef}
        color={settings.color}
        intensity={settings.intensity}
        width={settings.areaWidth}
        height={settings.areaHeight}
      />
    )
  }

  return (
    <spotLight
      ref={lightRef}
      target={targetRef.current}
      color={settings.color}
      intensity={settings.intensity}
      angle={settings.angle}
      penumbra={settings.penumbra}
      distance={settings.distance}
      decay={settings.decay}
      castShadow={false}
    />
  )
}

const ExpressiveLightHelper = ({ settings, targetPosition = [0, 0, 0] }) => {
  const groupRef = useRef()
  const markerRef = useRef()
  const lineRef = useRef()
  const pinStateRef = useRef({
    active: false,
    cameraAngle: 0,
  })
  const { camera } = useThree()
  const center = Array.isArray(targetPosition) ? targetPosition : [0, 0, 0]

  useFrame(() => {
    const cameraOrbitRadians = Math.atan2(camera.position.z - center[2], camera.position.x - center[0])

    if (settings.pinToCamera && !pinStateRef.current.active) {
      pinStateRef.current = {
        active: true,
        cameraAngle: cameraOrbitRadians,
      }
    }

    if (!settings.pinToCamera && pinStateRef.current.active) {
      pinStateRef.current.active = false
    }

    const position = getExpressiveLightPosition(settings, center, cameraOrbitRadians, pinStateRef.current.cameraAngle)
    groupRef.current?.position.set(position[0], position[1], position[2])
    groupRef.current?.lookAt(center[0], center[1], center[2])
    markerRef.current?.position.set(0, 0, 0)

    if (lineRef.current) {
      lineRef.current.geometry.setFromPoints([new THREE.Vector3(position[0], position[1], position[2]), new THREE.Vector3(center[0], center[1], center[2])])
    }
  })

  return (
    <group>
      <group ref={groupRef}>
        <Html position={[0, 0.38, 0]} center style={{ pointerEvents: "none" }}>
          <div
            style={{
              color: settings.color,
              fontFamily: "monospace",
              fontSize: 11,
              fontWeight: 700,
              textShadow: "0 1px 2px rgba(0,0,0,0.45)",
              whiteSpace: "nowrap",
            }}>
            {settings.name || settings.type}
          </div>
        </Html>
        <mesh ref={markerRef}>
          <sphereGeometry args={[0.12, 16, 16]} />
          <meshBasicMaterial color={settings.color} wireframe transparent opacity={0.95} depthTest={false} />
        </mesh>
        {settings.type === "spot" && (
          <mesh position={[0, 0, 0.9]} rotation={[-Math.PI / 2, 0, 0]}>
            <coneGeometry args={[Math.max(Math.tan(settings.angle) * 1.8, 0.15), 1.8, 32, 1, true]} />
            <meshBasicMaterial color={settings.color} wireframe transparent opacity={0.75} depthTest={false} />
          </mesh>
        )}
        {settings.type === "area" && (
          <mesh position={[0, 0, -0.04]}>
            <planeGeometry args={[settings.areaWidth * 0.18, settings.areaHeight * 0.18]} />
            <meshBasicMaterial color={settings.color} wireframe transparent opacity={0.8} depthTest={false} />
          </mesh>
        )}
      </group>
      <line ref={lineRef}>
        <bufferGeometry />
        <lineBasicMaterial color={settings.color} transparent opacity={0.65} depthTest={false} />
      </line>
    </group>
  )
}

function Viewer({ product }) {
  let clickTaleTagInjected = false

  const devMode = new URLSearchParams(window.location.search).get("dev") === "true"
  const noZoom = new URLSearchParams(window.location.search).get("noZoom")
  const closeButton = new URLSearchParams(window.location.search).get("closeButton")
  const [color, setColor] = useState(0)
  const { t } = useTranslation()
  const config = ModelConfig[product]
  const [{ reseller, analytics, locale, r, catalina, hide }, dispatchReseller] = useReseller()
  const [loaded, setLoaded] = useState(false)
  const [sparkled, setSparkled] = useState(false)
  const [idle, setIdle] = useState({ idle: false, target: null })
  const [update, setUpdate] = useState(true)
  const controls = useRef()
  const canvas = useRef()
  const [keyPressed, setKeyPressed] = useState(false)
  const [gen2MaterialColorOptions, setGen2MaterialColorOptions] = useState({})
  const [gen2MaterialSnapshot, setGen2MaterialSnapshot] = useState({})
  const [devSceneObjectGroups, setDevSceneObjectGroups] = useState({})
  const [devEditorActive, setDevEditorActive] = useState(false)
  const [renderSettings, setRenderSettings] = useState(() => getInitialRenderSettings(config, color))
  const [showExpressiveLights, setShowExpressiveLights] = useState(false)
  const [expressiveLightingMinimized, setExpressiveLightingMinimized] = useState(true)
  const [expressiveLights, setExpressiveLights] = useState(() => [getInitialExpressiveLighting(0)])
  const gen2MaterialColorOptionsDraft = useRef({})
  const gen2LiveMaterials = useRef({})

  useEffect(() => {
    setRenderSettings(getInitialRenderSettings(config, color))
  }, [product, color, config])

  useEffect(() => {
    const nextOptions = cloneConfigValue(config?.materialColorOptions)
    gen2MaterialColorOptionsDraft.current = nextOptions
    gen2LiveMaterials.current = {}
    setGen2MaterialColorOptions(nextOptions)
    setGen2MaterialSnapshot({})
    setDevSceneObjectGroups({})
  }, [product, config])

  useEffect(() => {
    if (!window.location.search.includes("analytics=true")) {
      return
    }

    if (!window.clickTaleTagInjected) {
      if (i18n.language === "en-US" || i18n.language === "en-CA" || i18n.language === "fr-CA") {
        !(function (d, t, u) {
          clickTaleTagInjected = true
          function injectTag() {
            var ns = d.createElementNS
            var a = ns ? ns.call(d, "http://www.w3.org/1999/xhtml", t) : d.createElement(t),
              s = d.getElementsByTagName(t)[0]
            a.async = true
            a.crossOrigin = "anonymous"
            a.type = "text/javascript"
            a.src = u
            s.parentNode.insertBefore(a, s)
          }
          if (d.readyState != "loading") {
            injectTag()
          } else {
            d.addEventListener("DOMContentLoaded", function () {
              setTimeout(injectTag, 0)
            })
          }
        })(document, "script", "https://t.contentsquare.net/uxa/5eebbd0a3ad46.js")
      } else {
        !(function (d, t, u) {
          clickTaleTagInjected = true
          function injectTag() {
            var ns = d.createElementNS
            var a = ns ? ns.call(d, "http://www.w3.org/1999/xhtml", t) : d.createElement(t),
              s = d.getElementsByTagName(t)[0]
            a.async = true
            a.crossOrigin = "anonymous"
            a.type = "text/javascript"
            a.src = u
            s.parentNode.insertBefore(a, s)
          }
          if (d.readyState != "loading") {
            injectTag()
          } else {
            d.addEventListener("DOMContentLoaded", function () {
              setTimeout(injectTag, 0)
            })
          }
        })(document, "script", "https://cdnssl.clicktale.net/www33/ptc/b529813b-3dee-4cf4-a877-00cb7f37f47a.js")
      }
    }
  }, [])

  // THREE.ColorManagement.enabled = false

  useEffect(() => {
    window.history.replaceState(null, "", `/${i18n.language}${window.location.search}`)
  }, [i18n.language])

  const idleModel = (currentTarget) => {
    setIdle((idle) => ({
      ...idle,
      idle: true,
      target: currentTarget,
    }))
    setTimeout(function () {
      setIdle((idle) => ({
        ...idle,
        idle: false,
        target: currentTarget,
      }))
    }, 10)
  }

  useEffect(() => {
    if (hide.includes("1")) {
      for (let i = 0; i < colorItems.length; i++) {
        if (!hide.includes(i + 1)) {
          setColor(i)
          break
        }
      }
    }
  }, [hide])

  function downHandler({ key }) {
    if (key === "ArrowLeft" && !keyPressed) {
      setUpdate(false)
      controls.current.rotateLeft(5)
      controls.current.update()
      setKeyPressed(() => ({
        ...true,
      }))
    }
    if (key === "ArrowUp" && !keyPressed) {
      setUpdate(false)
      controls.current.rotateUp(5)
      controls.current.update()
      setKeyPressed(() => ({
        ...true,
      }))
    }
    if (key === "-" && !keyPressed) {
      setUpdate(false)
      controls.current.dollyIn(1.1)
      controls.current.update()
      setKeyPressed(() => ({
        ...true,
      }))
    }
    if (key === "+" && !keyPressed) {
      setUpdate(false)
      controls.current.dollyOut(1.1)
      controls.current.update()
      setKeyPressed(() => ({
        ...true,
      }))
    }
    if (key === "ArrowDown" && !keyPressed) {
      setUpdate(false)
      controls.current.rotateUp(-5)
      controls.current.update()
      setKeyPressed(() => ({
        ...true,
      }))
    }
    if (key === "ArrowRight" && !keyPressed) {
      setUpdate(false)
      controls.current.rotateLeft(-5)
      controls.current.update()
      setKeyPressed(() => ({
        ...true,
      }))
    }
  }
  const upHandler = ({ key }) => {
    if (key === "ArrowLeft" || "ArrowRight" || "ArrowDown" || "ArrowUp" || "-" || "+") {
      setUpdate(false)
      setKeyPressed(() => ({
        ...false,
      }))
    }
  }

  const clickHandler = () => {
    setUpdate(true)
  }
  useEffect(() => {
    window.addEventListener("click", clickHandler)
    window.addEventListener("keydown", downHandler)
    window.addEventListener("keyup", upHandler)
    return () => {
      window.removeEventListener("keydown", downHandler)
      window.removeEventListener("keyup", upHandler)
    }
  }, [])

  const LoadedModel = () => {
    const { scene } = useGLTF(`/${config.model}.glb`)
    const textures = Array.isArray(config.dynamicTextures) ? config.dynamicTextures : []
    const dynamicTexturesName = config.dynamicTexturesName || {}
    const dynamicColor = Array.isArray(config.dynamicColor) ? config.dynamicColor : []
    const logoGroup = Array.isArray(config.logoGroup) ? config.logoGroup : config.logoGroup ? [config.logoGroup] : []
    const gen2ColorAnimations = useRef(new Map())
    const activeGen2MaterialColorOptions =
      config.generation === 2 && devMode && Object.keys(gen2MaterialColorOptionsDraft.current).length > 0
        ? gen2MaterialColorOptionsDraft.current
        : config.generation === 2 && Object.keys(gen2MaterialColorOptions).length > 0
        ? gen2MaterialColorOptions
        : config.materialColorOptions || {}
    let textureMap = []
    if (textures.length > 0) {
      textures.forEach(function (item, index) {
        item = useLoader(TextureLoader, `/${config.model}-${index}-${config.textureNames[color]}.jpg`)
        item.colorSpace = SRGBColorSpace

        item.flipY = false
        item.wrapS = RepeatWrapping
        item.wrapT = RepeatWrapping
        textureMap.push(item)
      })
    }

    useFrame(() => {
      gen2ColorAnimations.current.forEach((animation, id) => {
        const progress = getEaseOutProgress(animation.startTime)
        animation.material.color.lerpColors(animation.from, animation.to, progress)
        animation.material.needsUpdate = true
        if (progress >= 1) {
          animation.material.color.copy(animation.to)
          gen2ColorAnimations.current.delete(id)
        }
      })
    })

    useEffect(() => {
      const nextGen2MaterialSnapshot = {}
      const nextSceneObjectGroups = {}
      const gen2TrackedMaterialNames = new Set(getGen2MaterialNames(activeGen2MaterialColorOptions))

      scene.traverse(function (child) {
        
        if (child.material) {
          if (devMode && config.generation === 2 && child.isMesh) {
            getMaterialNames(child.material).forEach((materialName) => {
              if (!nextSceneObjectGroups[materialName]) {
                nextSceneObjectGroups[materialName] = []
              }
              nextSceneObjectGroups[materialName].push(child.name || child.uuid)
            })
          }

          const materials = Array.isArray(child.material) ? child.material : [child.material]
          materials.forEach((material) => {
            material.dithering = true
            material.toneMapped = false
            material.envMapIntensity = renderSettings.envmap
            normalizeOpaqueMaterialState(material)
            material.needsUpdate = true
          })
        }
        if (child.name === "Plane") {
          

          child.material.opacity =
            config.model === "zasa" || product === "flex2" || product === "flex" || product === "slhome" || product === "slmax" || product === "slplus" || product === "lus" || product === "lusb" || product === "lusw" ? 1 : 0.1

          child.material.toneMapped = false
          child.material.transparent = true
          child.material.depthWrite = config.model === "zasa" ? false : child.material.depthWrite
          child.material.needsUpdate = true
          // child.material.blending = THREE.MultiplyBlending
        }
        if (config.model !== "zasa" && child.name.includes("BT_Logo")) {
          child.material.color = { r: 0.300000011920929, g: 0.300000011920929, b: 0.300000011920929 }
        }
        if (textures.includes(child.name)) {
          const texture = textureMap[dynamicTexturesName[child.name]]
          texture.colorSpace = SRGBColorSpace
          child.material.dithering = true
          texture.wrapS = RepeatWrapping
          texture.wrapT = RepeatWrapping
          texture.flipY = false

          child.material.envMapIntensity = renderSettings.envmap
          child.material.map = texture
        }
        if (product === "lus" && child.name === "bottomRing") {
          if (!child.material.userData.donnieOriginalMapStored) {
            child.material.userData.donnieOriginalMap = child.material.map || null
            child.material.userData.donnieOriginalMapStored = true
          }

          if (config.colorNames[color] === "Driftwood Sand") {
            child.material.color = { r: 0.6, g: 0.6, b: 0.6 }
            child.material.map = getDonnieWoodTexture()
          } else {
            child.material.color = config.materialValues[color]
            child.material.map = child.material.userData.donnieOriginalMap
          }
          child.material.needsUpdate = true
        }
        if (config.generation === 2 && child.material) {
          // GLTFLoader removes dots from object names (caps.001 becomes caps001).
          const isSabrinaSecondCap = config.model === "sabrina" && (child.name === "caps.001" || child.name === "caps001")

          if (config.model === "sabrina") {
            const materials = Array.isArray(child.material) ? child.material : [child.material]
            const updatedMaterials = materials.map((material) => {
              if (isSabrinaSecondCap && !material.userData?.sabrinaSecondCapMaterial) {
                const capMaterial = material.clone()
                capMaterial.userData.sabrinaSecondCapMaterial = true
                return capMaterial
              }
              if (material.name !== SABRINA_FROSTED_MATERIAL || material.userData?.sabrinaFrostedMaterial) return material
              return createSabrinaFrostedMaterial(material)
            })

            child.material = Array.isArray(child.material) ? updatedMaterials : updatedMaterials[0]
          }

          if (config.model === "spice") {
            const materials = Array.isArray(child.material) ? child.material : [child.material]
            const updatedMaterials = materials.map((material) => {
              if (!SPICE_TRANSPARENT_MATERIALS.includes(material.name) || material.userData?.spiceTransparentMaterial) return material
              return createSpiceTransparentMaterial(material)
            })

            child.material = Array.isArray(child.material) ? updatedMaterials : updatedMaterials[0]
          }

          const materials = Array.isArray(child.material) ? child.material : [child.material]
          materials.forEach((material) => {
            let materialColor =
              config.model === "spice"
                ? findSpiceMaterialColor(config, color, material.name, activeGen2MaterialColorOptions)
                : findGen2MaterialColor(config, color, material.name, activeGen2MaterialColorOptions)
            if (!materialColor) return

            if (!gen2LiveMaterials.current[material.name]) {
              gen2LiveMaterials.current[material.name] = new Set()
            }
            gen2LiveMaterials.current[material.name].add(material)

            const isMariahYolkGradient =
              config.model === "mariah" && material.name === "plasticYolks" && config.colorNames[color] === MARIAH_YOLK_GRADIENT_COLOR

            if (config.model === "mariah" && material.name === "plasticYolks") {
              if (isMariahYolkGradient) {
                material.map = getMariahYolkGradientTexture(material)
                materialColor = "#FFFFFF"
              } else if (material.map === material.userData?.mariahYolkGradientTexture) {
                material.map = null
              }
              material.needsUpdate = true
            }

            if (config.model === "mariah" && material.name === MARIAH_SCRIM_TEXTURE_MATERIAL) {
              const isSkyPinkScrimTexture = config.colorNames[color] === MARIAH_SCRIM_TEXTURE_COLOR
              const scrimTexture = isSkyPinkScrimTexture ? getMariahScrimTexture() : mariahScrimTexture

              if (isSkyPinkScrimTexture) {
                material.map = scrimTexture
                materialColor = "#FFFFFF"
              } else if (scrimTexture && material.map === scrimTexture) {
                material.map = null
              }
              material.needsUpdate = true
            }

            if (config.model === "aubrey") {
              applyAubreyMetalnessOverrides(material, config.colorNames[color])
              if (material.name === "plate") {
                if (typeof material.userData.aubreyOriginalRoughness !== "number") {
                  material.userData.aubreyOriginalRoughness = material.roughness
                }
                material.roughness = config.colorNames[color] === "Sky Pink" ? 0.6 : material.userData.aubreyOriginalRoughness
              }
              material.needsUpdate = true
            }

            const targetColor = new THREE.Color(normalizeHexColor(materialColor))
            if (isSabrinaSecondCap && config.colorNames[color] === "Soft Lilac") {
              targetColor.lerp(new THREE.Color("#FFFFFF"), 0.08)
            }
            const existingAnimation = gen2ColorAnimations.current.get(material.uuid)
            if (existingAnimation) {
              const progress = getEaseOutProgress(existingAnimation.startTime)
              material.color.lerpColors(existingAnimation.from, existingAnimation.to, progress)
            }

            if (material.color.equals(targetColor)) {
              gen2ColorAnimations.current.delete(material.uuid)
              return
            }

            if (existingAnimation && existingAnimation.to.equals(targetColor)) return

            gen2ColorAnimations.current.set(material.uuid, {
              material,
              from: material.color.clone(),
              to: targetColor,
              startTime: performance.now(),
            })
          })
        }
        if (devMode && config.generation === 2 && child.material) {
          const materials = Array.isArray(child.material) ? child.material : [child.material]
          materials.forEach((material) => {
            if (!gen2TrackedMaterialNames.has(material.name)) return
            nextGen2MaterialSnapshot[material.name] = getMaterialSnapshot(material)
          })
        }
        if (product === "qcue2") {
          if (child.name === `case` || child.name === "inner") {
            child.material.metalness = config.bodyMetalness[color]
          }
        }
        
           if (product === "slmicro2") {
            if (child.name == `blocker`) {
           
              child.material.color = color === 3 ? {"r": 0.52, "g": 0.25 , "b": 0.328} : { "r": 0.011, "g": 0.011, "b": 0.011 } 
            }
              if (child.name == `strap-black`) { 
              child.visible = color === 0 ? true : false 
            }
            if (child.name == `strap-blue`) {
              child.visible = false
              child.visible = color === 1 ? true : false
            }
            if (child.name == `strap-sand`) {
               child.visible = false
               child.visible = color === 2 ? true : false
            }
            if (child.name == `strap-petal`) {
               child.visible = false
               child.visible = color === 3 ? true : false
            }
            if (child.name == `strap-twilight`) {
               child.visible = false
               child.visible = color === 4 ? true : false
            }
             if (child.name == `strap-orange`) {
               child.visible = false
               child.visible = color === 5 ? true : false
            }
              if (child.name == `strap-dew`) {
               child.visible = false
               child.visible = color === 6 ? true : false
            }
          }

        if (product === "flex2") {
          
          if (child.name === `Plane001`) {
          
            child.material.color =  {"r": 0, "g": 0 , "b": 0} 
            child.material.emissiveIntensity = 0.15
           
          }
          if (child.name === `bottom`) {
            child.material.metalness = 0.3
          }
        }


        if (child.name === "logo" && product === "lus") {
          child.material.color = config.materialValuesExtra[color]
        }

        if (product === "qcuhp") {
          if (child.name.includes("BT_Logo")) {
            if (color === 0 || color === 3) {
              child.material.color = { r: 0.100000011920929, g: 0.100000011920929, b: 0.100000011920929 }
            }
            if (color === 5) {
              child.material.color = { r: 0.35, g: 0.21, b: 0.3 }
            } else child.material.color = { r: 0.100000011920929, g: 0.100000011920929, b: 0.100000011920929 }
          }
        }

        if (product === "qcuhp2") {
          if (child.name.includes("BT_Logo")) {
            if (color === 3) {
              child.material.color = { r: 0.32, g: 0.24, b: 0.51 }
              return
            }

            if (color === 2) {
              child.material.color = { r: 0.23, g: 0.16, b: 0.1 }
              return
            }
            if (color === 1) {
              child.material.color = { r: 0.25, g: 0.25, b: 0.25 }
              return
            }
            if (color === 5) {
              child.material.color = { "r": 0.2221387722, "g": 0.1528606470, "b": 0.1648032667}
              return
            }
             if (color === 6) {
              child.material.color = { "r": 0.25, "g": 0.25, "b": 0.11}
              return
            }
            child.material.color = { r: 0.100000011920929, g: 0.100000011920929, b: 0.100000011920929 }
          }
        }

        if (child.name === `R_Marking` && product === "qcueb") {
          child.material.color = config.materialValuesExtra[color]
        }
        if (child.name === `strapCarrier` || (child.name === "pSphere" && product === "slmicro")) {
          child.material.color = config.materialValuesExtra[color]
        }

        if (child.name === `BackLogo` && product === "qce") {
          child.material.color = config.logoColor?.[color] || config.materialValuesExtra[color]
        }
        if (product === "qcuhp" && child.name == "Left_Cushion_Remesh") {
          child.material.color = config.materialValuesLeather[color]
          child.material.metalness = color === 5 ? 0.4 : 0
        }

        if (child.name == "B_2-Black" && product === "uoeb") {
          child.material.color = config.materialText[color]
          child.material.metalness = 1
        }
        if (product === "uoeb") {
          if (
            child.name == `Case_Remeshed-Black` ||
            child.name == `Metal_Front_Plate_Remesh-Black` ||
            child.name == `Metal_Front_Plate_Remesh_2-Black` ||
            child.name == `Lid_remesh-Black` ||
            child.name == "Button_Simplified_remesh-Black" ||
            child.name == "Button_Simplified_Remesh_2-Black" ||
            child.name == "Button_Simplified_Remesh-Black"
          ) {
            if (!(child.material instanceof THREE.MeshPhysicalMaterial)) {
              child.material = new THREE.MeshPhysicalMaterial({})
              const flakes = new TextureLoader().load("flakes.jpg")
              flakes.wrapS = RepeatWrapping
              flakes.flipY = false
              // flakes.encoding = 3001
              flakes.wrapT = RepeatWrapping
              flakes.repeat.set(15, 15)
              child.material.normalMap = flakes
            }

            child.material.toneMapped = false
            child.material.color = config.materialValues[color]
            child.material.roughness = config.cupRoughness[color]
            child.material.metalness = config.materialMetalness[color]
            child.material.normalScale.set(config.normal[color], config.normal[color])
            child.material.clearcoat = config.clearcoat[color]
            child.material.clearcoatRoughness = config.clearcoatRoughness[color]
            child.material.reflectivity = 0.6
            child.material.envMapIntensity = renderSettings.envmap
            child.material.dithering = true
            child.material.iridescenceIOR = 1.43
            child.material.toneMapped = false
            child.material.iridescence = config.materialIridescence[color]
            child.material.transparent = true
            child.material.iridescenceThicknessRange = [0, config.materialThickness[color]]
            child.material.specularTint = config.sheenColor[color]
            child.material.specularColor = config.sheenColor[color]
            child.material.specularIntensity = config.sheenIntensity[color]

            if (child.name === "Metal_Front_Plate_Remesh-Black" || child.name === "Metal_Front_Plate_Remesh_2-Black") {
              // Check if the texture has already been loaded and stored on the child

              if (!child._gradientTexture) {
                // Only load if it hasn't been loaded already
                child._gradientTexture = new TextureLoader().load("gradientUOEB.jpg")
                child._gradientTexture.flipY = false
              }

              // Apply or remove the texture based on the color condition
              if (color === 9) {
                child.material.color = config.materialValuesCap[color]
                child.material.metalness = 1
              }

              if (color === 7) {
                child.material.map = child._gradientTexture
                child.material.needsUpdate = true // Inform Three.js that the material has changed
              } else {
                child.material.map = null // Set the map to null to remove the texture
                child.material.needsUpdate = true // Inform Three.js to update the material
              }
            }

            if ((child.name == "Button_Simplified_Remesh_2-Black" || child.name == "Button_Simplified_Remesh-Black") && color == 9) {
              child.material.color = config.materialValuesCap[color]
              child.material.metalness = 1
            }
            if ((child.name == "Button_Simplified_Remesh_2-Black" || child.name == "Button_Simplified_Remesh-Black") && color == 11) {
              child.material.color = config.materialValuesCap[color]
              child.material.metalness = 1
              child.material.roughness = 0.2
            }
            if ((child.name == "Button_Simplified_Remesh_2-Black" || child.name == "Button_Simplified_Remesh-Black") && color == 10) {
              // child.material.color = config.materialValuesCap[color]
              child.material.metalness = 0.65
              child.material.roughness = 0.1
            }
            if ((child.name == "Button_Simplified_Remesh_2-Black" || child.name == "Button_Simplified_Remesh-Black") && color == 12) {
              child.material.color = config.materialValuesCap[color]
              child.material.metalness = 1
              child.material.roughness = 0.1
            }
            if ((child.name == "Button_Simplified_Remesh_2-Black" || child.name == "Button_Simplified_Remesh-Black") && color == 13) {
              child.material.color = config.materialValuesCap[color]
              child.material.metalness = 1
              child.material.roughness = 0.1
            }

            if ((child.name == "Button_Simplified_Remesh_2-Black" || child.name == "Button_Simplified_Remesh-Black") && color == 8) {
              child.material.color = config.materialValuesCap[color]
              child.material.metalness = 1
            }
            if ((child.name == "Button_Simplified_Remesh_2-Black" || child.name == "Button_Simplified_Remesh-Black") && color == 7) {
              child.material.color = config.materialValuesCap[color]
              child.material.metalness = 0
            }
          }
          let sparkles = null
          sparkles = new TextureLoader().load("sparkles.jpg")
          sparkles.wrapT = RepeatWrapping
          sparkles.wrapS = RepeatWrapping
          sparkles.repeat.set(15.4, 15.4)

          let sparklesr = null
          sparklesr = new TextureLoader().load("sparkles-r.jpg")
          sparklesr.wrapT = RepeatWrapping
          sparklesr.wrapS = RepeatWrapping
          sparklesr.repeat.set(15.4, 15.4)

          // Later in your logic
          if (
            child.name === "Case_Remeshed-Black" ||
            child.name === "Lid_remesh-Black" ||
            child.name === "Button_Simplified_remesh-Black"
          ) {
            if (color === 12 && !sparkled) {
              // Only apply sparkles if it's not already applied
              if (child.material.map !== sparkles) {
                child.material.color = { r: 1, g: 1, b: 1 }
                child.material.map = sparkles
                child.material.roughnessMap = sparklesr
                child.material.metalnessMap = sparklesr
                child.material.roughness = 0.2
                setSparkled(true)
              }
            }
            if (color !== 12) {
              child.material.roughnessMap = null
              child.material.metalnessMap = null
              child.material.map = null
              child.material.color = config.materialValues[color]
              setSparkled(false)
            }
          }
        }
        if (child.name === "Main_Shell_Remesh_2-Black" || child.name === "Main_Shell_Remesh-Black") {
          child.material = new THREE.MeshStandardMaterial({})
          child.material.color = config.materialValuesExtraBud[color]
          child.material.roughness = config.cupRoughness[color]
          // child.material.metalness = 1
        }
        if (child.name === "Top_Cap_Simplified_Remesh-Black") {
          child.material.color = config.materialValuesNeck[color]
        }

        if (dynamicColor.includes(child.name)) {
          if (
            product === "qce" &&
            (QCE_DEWDROP_DARK_PARTS.includes(child.name) || QCE_MAUVE_OVERRIDE_PARTS.includes(child.name)) &&
            !child.material.userData?.qcePartOnlyMaterial
          ) {
            child.material = child.material.clone()
            child.material.userData.qcePartOnlyMaterial = true
          }
          child.material.toneMapped = false
          child.material.envMapIntensity = renderSettings.envmap
          child.material.dithering = true
          child.material.color = config.materialValues[color]
          if (product === "qce" && QCE_DEWDROP_DARK_PARTS.includes(child.name) && config.colorNames[color] === "Dewdrop Mint") {
            child.material.color = QCE_DEWDROP_DARK_COLOR
          }
          if (product === "qce" && QCE_MAUVE_OVERRIDE_PARTS.includes(child.name) && config.colorNames[color] === "Rosewood Mauve") {
            child.material.color = QCE_MAUVE_OVERRIDE_COLOR
          }
          if (child.name == `duran_headband_GRP_right_hinge` || child.name == `holes`) {
            child.material.color = config.materialValuesExtra[color]
          }

          if (product === "qcueb" && (child.name == `inner001`) || ("qcue2" && (child.name == `case` || child.name == `inner001`))) {
            const newMat = new MeshPhysicalMaterial({})
            child.material = newMat
  
            let sparkles = null
            sparkles = new TextureLoader().load("sparkles.jpg")
            sparkles.wrapT = RepeatWrapping
            sparkles.wrapS = RepeatWrapping
            sparkles.repeat.set(2, 2)

            let sparklesr = null
            sparklesr = new TextureLoader().load("sparkles-r.jpg")
            sparklesr.wrapT = RepeatWrapping
            sparklesr.wrapS = RepeatWrapping
            sparklesr.repeat.set(2, 2)

            child.material.envMapIntensity = renderSettings.envmap
            child.material.dithering = true
            // child.material.iridescence = 1
            child.material.roughness = config.cupRoughness[color]
            // child.material.specularTint = config.sheenColor[color]
            child.material.specularIntensity = config.sheenIntensity[color]
            child.material.color = config.materialValues[color]
            child.material.specularColor = config.sheenColor[color]
            

                    if (product === "qcue2") {
           
              if (child.name === `case` || `inner001`) {
                child.material.map = color === 4 ? sparkles : null
                child.material.roughnessMap = color === 4 ? sparklesr : null
                child.material.metalnessMap = color === 4 ? sparklesr : null
              }
              child.material.metalness = config.padMetalness[color]
              }
              }
              if (child.name == `padX` || child.name == `padL`) {
                 let sparkles = null
            sparkles = new TextureLoader().load("sparkles.jpg")
            sparkles.wrapT = RepeatWrapping
            sparkles.wrapS = RepeatWrapping
            sparkles.repeat.set(2, 2)

            let sparklesr = null
            sparklesr = new TextureLoader().load("sparkles-r.jpg")
            sparklesr.wrapT = RepeatWrapping
            sparklesr.wrapS = RepeatWrapping
            sparklesr.repeat.set(2, 2)
             
                const newMatPad = new MeshPhysicalMaterial({})

                child.material = newMatPad
                const texture = textureMap[dynamicTexturesName[child.name]]
                child.material.map = texture
                child.material.dithering = true
                texture.wrapS = RepeatWrapping
                texture.wrapT = RepeatWrapping
                texture.flipY = false
                // child.material.metalness = config.padMetalness[color]
                child.material.envMapIntensity = config.envmap2[color]
                child.material.roughness = config.cupRoughness2[color]
                child.material.specularColor = config.sheenColor[color]
                child.material.specularTint = config.sheenColor[color]
                child.material.specularIntensity = config.sheenIntensity2[color]
                // child.material.map = color === 4 ? sparkles : null
                child.material.roughnessMap = color === 4 ? sparklesr : null
                child.material.metalnessMap = color === 4 ? sparklesr : null
              }
            
            
    
          
          
       

          if (child.name == `Inside_Lid_remesh-Black` || child.name == `Tray_Remeshed-Black` || child.name == "Main_Shell_Remesh-Black") {
            child.material.color = config.materialValuesExtra[color]
          }

          if (child.name == `Headband_Curshion_Remesh_v2`) {
            child.material.normalScale.set(1.2, 1.2)
          }
          if (product == "slmicro2") {
           

      
            let petalTexture = null
            petalTexture = new TextureLoader().load("petalPinkBody.jpg")
            petalTexture.flipY = false
            petalTexture.repeat.set(1, 1)
            if (child.name == `body`) {
              if (color === 3) {
              child.material.map = petalTexture;
              }
              else child.material.map = null;
            }
          }

          if (product == "qcuhp") {
            if (child.name == `Arm_R_Remesh` || child.name === `Hinge_R_Remesh`) {
              child.material.color = config.materialValuesExtra[color]
              child.material.roughness = config.roughnessScrim[color]
              if (color === 4) {
                child.material.normalScale.set(0.1, -0.1)
              } else {
                child.material.normalScale.set(0.86, -0.86)
              }
            }

            // child.material.normalMap = null
          }

          if (
            product == "qcuhp" &&
            (child.name == `L_Earcup_Remesh` || child.name == `R_Earcup_Remesh` || child.name == `Headband_Top_Remesh`)
            // child.name == `BT_Button_Remesh` ||
            // child.name == `Volume_Slider_Remesh`
          ) {
            child.material = new MeshPhysicalMaterial({})

            child.material.envMapIntensity = renderSettings.envmap
            child.material.dithering = true
            child.material.roughness = config.cupRoughness[color]
            child.material.side = DoubleSide
            child.material.specularColor = config.sheenColor[color]
            child.material.color = config.materialValues[color]
            // child.material.reflectivity = 1
            // child.material.specularTint = config.sheenColor[color]
            child.material.specularIntensity = config.sheenIntensity[color]
            child.material.metalness = color === 5 ? 0.39 : 0
          }

          if (
            product == "qcuhp2" &&
            (child.name == `L_Earcup_Remesh` || child.name == `R_Earcup_Remesh` || child.name == `Headband_Top_Remesh`)
            // child.name == `BT_Button_Remesh` ||
            // child.name == `Volume_Slider_Remesh`
          ) {
            let sparkles = null
            sparkles = new TextureLoader().load("sparkles.jpg")
            sparkles.wrapT = RepeatWrapping
            sparkles.wrapS = RepeatWrapping
            sparkles.repeat.set(15.4, 15.4)

            let sparklesr = null
            sparklesr = new TextureLoader().load("sparkles-r.jpg")
            sparklesr.wrapT = RepeatWrapping
            sparklesr.wrapS = RepeatWrapping
            sparklesr.repeat.set(15.4, 15.4)

            child.material = new MeshPhysicalMaterial({})

            child.material.envMapIntensity = renderSettings.envmap
            child.material.dithering = true
            child.material.roughness = config.cupRoughness[color]
            child.material.side = DoubleSide
            child.material.specularColor = config.sheenColor[color]
            child.material.color = config.materialValues[color]
            // child.material.reflectivity = 1
            // child.material.specularTint = config.sheenColor[color]
            child.material.specularIntensity = config.sheenIntensity[color]
            child.material.metalness = 0

            child.material.map = color === 4 ? sparkles : null
            child.material.roughnessMap = color === 4 ? sparklesr : null
            child.material.metalnessMap = color === 4 ? sparklesr : null

            // if (color !== 4) {
            //   child.material.roughnessMap = null
            //   child.material.metalnessMap = null
            //   child.material.map = null
            //   child.material.color = config.materialValues[color]
            //   setSparkled(false)

            // }
          }

          if (child.name == `loneScrims`) {
            child.material.color = config.materialInnerScrim[color]
          }
          if (product == "qcuhp2") {
            if (child.name == `Arm_R_Remesh` || child.name === `Hinge_R_Remesh`) {
              child.material.color = config.materialValuesExtra[color]
              child.material.roughness = config.roughnessScrim[color]
              child.material.normalScale.set(0.05, -0.05)
            }
          }
        }
        if (child.name === `Hinge_R_Remesh`) {
          child.material.color = config.materialValuesExtra[color]
        }
        if (child.name === `Scrim_R_Remesh`) {
          if (color === 5 && product !== "qcuhp2") {
            child.material.metalness = 1
          } else child.material.metalness = 0.4
        }
        if (child.name == `Multi_Action_Buttom_remesh`) {
          child.material.metalness = color === 5 && product !== "qcuhp2" ? 0.59 : 0
        }
        if (product === "qcheadphones") {
          if (child.name.includes("rightEarcup")) {
            child.material.normalScale.set(2.05, -2.05)
          }
        }
        if (product == "qcuhp2") {
          if (child.name == `Arm_R_Remesh` || child.name === `Hinge_R_Remesh`) {
            child.material.color = config.materialValuesExtra[color]
            child.material.roughness = config.roughnessScrim[color]
            child.material.normalScale.set(0.05, -0.05)
            /////
          }
        }

        if (product === "uoeb") {
          if (child.name.includes("Bose_Logo_OG-Black")) {
            child.material.metalness = config.logoMetalness[color]
            child.material.color = config.logoColor[color]
          }

          if ((child.name == "Metal_Front_Plate_Remesh_2-Black" || child.name == `Metal_Front_Plate_Remesh-Black`) && color == 9) {
            child.material.color = config.materialValuesCap[color]
          }

          if ((child.name == "Metal_Front_Plate_Remesh_2-Black" || child.name == `Metal_Front_Plate_Remesh-Black`) && color == 10) {
            child.material.metalness = 0.75
            child.material.roughness = 0.08
          }

          if ((child.name == "Metal_Front_Plate_Remesh_2-Black" || child.name == `Metal_Front_Plate_Remesh-Black`) && color == 11) {
            child.material.color = config.materialValuesCap[color]
            child.material.metalness = 0.78
            child.material.roughness = 0.09
          }
          if ((child.name == "Metal_Front_Plate_Remesh_2-Black" || child.name == `Metal_Front_Plate_Remesh-Black`) && color == 12) {
            child.material.color = config.materialValuesCap[color]
            child.material.metalness = 0.75
            child.material.roughness = 0.09
          }
          if ((child.name == "Metal_Front_Plate_Remesh_2-Black" || child.name == `Metal_Front_Plate_Remesh-Black`) && color == 13) {
            child.material.color = config.materialValuesCap[color]
            child.material.metalness = 0.75
            child.material.roughness = 0.06
          }
          if (child.name == "Tray_Remeshed-Black") {
            if (color == 11) {
              // child.material.color = config.materialValuesCap[color]
              child.material.roughness = 0.1
              child.material.metalness = 0
              // child.material.envMapIntensity = 1.5
            } else {
              child.material.roughness = 0.1
              child.material.metalness = 0
              child.material.envMapIntensity = renderSettings.envmap
            }
          }
        }
        if (logoGroup.includes(child.name)) {
          if (product === "qcheadphones") {
            child.material.metalness = config.logoMetalness[color]
            child.material.roughness = 0.01
          }
          if (child.name.includes("sb900logo")) {
            if (child.name == `sb900logo-${config.textureNames[color]}`) {
              child.visible = true
            } else {
              child.visible = false
            }
            return
          }

          if (child.name.includes("sb900logo")) {
            if (child.name == `sb900logo-${config.textureNames[color]}`) {
              child.visible = true
            } else {
              child.visible = false
            }
            return
          }

          child.material.color = config.logoColor[color]
          child.material.metalness = config.logoMetalness[color]

          if (product === "qce2") {
            child.material.roughness = config.logoRoughness[color]
            return
          }
        }
      })

      setLoaded(true)
      if (devMode && config.generation === 2) {
        setGen2MaterialSnapshot((current) => {
          return JSON.stringify(current) === JSON.stringify(nextGen2MaterialSnapshot) ? current : nextGen2MaterialSnapshot
        })
        setDevSceneObjectGroups((current) => {
          return JSON.stringify(current) === JSON.stringify(nextSceneObjectGroups) ? current : nextSceneObjectGroups
        })
      }
    }, [color, activeGen2MaterialColorOptions, devMode, renderSettings.envmap])

    return (
      <primitive object={scene} dispose={null} scale={config.scale} position={config.position} rotation={config.rotation} visible={true} />
    )
  }
  function Loader() {
    const { progress } = useProgress()
    return (
      <Html center style={{ fontSize: "34px", width: "100px", height: "100px" }}>
        <CircularProgressbar
          styles={buildStyles({
            strokeLinecap: "butt",
            textColor: "rgb(36, 36, 36)",
            pathColor: "rgb(36, 36, 36)",
            trailColor: "transparent",
          })}
          value={progress.toFixed(0)}
          text={`${progress.toFixed(0)}%`}
        />
      </Html>
    )
  }

  const Shadow = () => {
    if (!config.shadow || !renderSettings.shadow) return null

    const { scene } = useGLTF(config.shadow)
    scene.traverse((child) => {
      if (child.isMesh) {
        child.material.needsUpdate = true

        child.material.transparent = true // Enable transparency for blending
        // child.material.blending = THREE.MultiplyBlending
      }
    })
    return <primitive object={scene} scale={config.shadowScale} position={config.position} rotation={config.rotation} />
  }
  const CameraControls = () => {
    let distance
    let offset
    const {
      camera,
      gl: { domElement },
    } = useThree()

    useFrame(() => {
      if (devEditorActive) return

      if (update) {
        // console.log(camera.position)
        controls.current.update()
      }

      if (config.wide) {
        if (idle.idle) {
          controls.current.target.x = idle.target.x
          return
        }
        if (product === "edelman") {
          distance = camera.position.distanceTo(controls.current.target) / 10
          offset = camera.position.x / 5
          if (offset < -0.01 || offset > 0.01) {
            controls.current.target.x = camera.position.x / (7.2 * distance)
          }
          return
        }
        distance = camera.position.distanceTo(controls.current.target) / 10
        offset = camera.position.x / 5
        if (offset < -0.01 || offset > 0.01) {
          controls.current.target.x = camera.position.x / (4.2 * distance)
        }
      }
    })
    return (
      <orbitControls
        ref={controls}
        args={[camera, domElement]}
        enabled={!devEditorActive}
        enableZoom={noZoom ? false : true}
        enableKeys={true}
        autoRotate={false}
        enablePan={false}
        target={idle.target}
        rotateSpeed={isMobile ? 1.2 : config.cameraSpeed}
        enableDamping={true}
        dampingFactor={0.05}
        maxDistance={30}
        minDistance={config.minDistance}
        maxPolarAngle={["qcuhp", "qcuhp2", "uoeb", "qcue2", "slmicro2", "qcheadphones", "qch2"].includes(product) ? Math.PI : Math.PI / 2}
      />
    )
  }
  // const noPorterLocales = [
  //   "de-DE",
  //   "en-CA",
  //   "en-GB",
  //   "en-US",
  //   "en-AE",
  //   "en-AU",
  //   "en-HK",
  //   "en-IN",
  //   "en-IE",
  //   "en-NZ",
  //   "en-SG",
  //   "fr-CA",
  //   "fr-CH",
  //   "fr-LU",
  //   "fr-BE",
  //   "fr-FR",
  //   "de-AT",
  //   "de-CH",
  //   "zh-HK",
  //   "zh-TW",
  //   "zh-CN",
  //   "da-DK",
  //   "nl-NL",
  //   "nl-BE",
  //   "fi-FI",
  //   "hu-HU",
  //   "it-IT",
  //   "it-CH",
  //   "ja-JP",
  //   "nb-NO",
  //   "pl-PL",
  //   "es-ES",
  //   "es-MX",
  //   "es-AR",
  //   "es-CL",
  //   "es-CO",
  //   "es-PE",
  //   "sv-SE",
  //   "vi-VN",
  // ]
  // EU release
  const iceFlex = ["en-US", "en-CA", "fr-CA"]
  const qcheadphoneIce = ["en-US", "en-CA", "fr-CA", "fr-FR", "de-DE", "en-GB"]
  const noPorterLocales = ["en-AE", "en-HK", "en-IN", "en-SG", "zh-HK", "zh-TW", "zh-CN", "vi-VN"]
  const CypressLocales = ["es-ES", "hu-HU"]
  const moonStonesLocalesQcueb = ["en-US", "ja-JP", "en-CA", "fr-CA"]
  const noQC45Locales = ["es-AR", "es-CL", "es-CO", "es-PE"]
  // const noSandStoneLocales = ["es-MX", "es-CL", "es-CO", "es-AR", "es-PE", "ja-JP", "hu-HU", "es-ES", "en-AE", "zh-TW", "zh-HK", "en-HK"]
  const noSandStoneLocales = [""]
  const flexLocales = [
    "en-US",
    "es-MX",
    "zh-TW",
    "zh-HK",
    "en-CA",
    "fr-CA",
    "en-AU",
    "en-NZ",
    "en-IN",
    "en-SG",
    "en-AE",
    "en-HK",
    "de-AT",
    "de-CH",
    "de-DE",
    "da-DK",
    "en-GB",
    "en-IE",
    "es-ES",
    "fr-BE",
    "fr-CH",
    "fr-FR",
    "fr-LU",
    "it-IT",
    "it-CH",
    "hu-HU",
    "nl-NL",
    "nl-BE",
    "nb-NO",
    "ja-JP",
    "pl-PL",
    "fi-FI",
    "sv-SE",
  ]
  const flexLocales2 = [
    "en-US",
    "en-CA",
    "fr-CA",
    "ja-JP",
    "en-NZ",
    "en-AU",
    "en-GB",
    "de-DE",
    "fr-FR",
    "es-AR",
    "es-CL",
    "es-CO",
    "es-MX",
    "es-PE",
    "de-AT",
    "de-CH",
    "fr-CH",
    "it-CH",
    "da-DK",
    "en-IE",
    "es-ES",
    "fr-BE",
    "nl-BE",
    "it-IT",
    "nl-NL",
    "pl-PL",
    "nb-NO",
    "fi-FI",
    "sv-SE",
  ]
  const diamondLocales = [
    "en-US",
    "en-CA",
    "fr-CA",
    "fr-FR",
    "de-DE",
    "en-GB",
    "nl-BE",
    "fr-BE",
    "en-IE",
    "it-IT",
    "nl-NL",
    "de-AT",
    "sv-SE",
    "en-AU",
    "en-NZ",
    "ja-JP",
    "zh-HK",
    "zh-TW",
    "en-HK",
    "de-CH",
    "fr-CH",
    "it-CH",
    "da-DK",
    "es-ES",
    "fr-LU",
    "fr-LU",
    "it-IT",
    "nb-NO",
    "pl-PL",
    "fi-FI",
    "sv-SE",
    "hu-HU",
  ]

  const lunarLocales = [
    "en-US",
    "ja-JP",
    "en-CA",
    "fr-CA",
    "de-DE",
    "fr-FR",
    "en-GB",
    "en-AU",
    "en-NZ",
    "de-AT",
    "de-CH",
    "fr-CH",
    "it-CH",
    "da-DK",
    "en-HK",
    "zh-HK",
    "zh-TW",
    "en-IE",
    "es-ES",
    "nl-BE",
    "nl-NL",
    "fr-BE",
    "fr-LU",
    "it-IT",
    "nb-NO",
    "pl-PL",
    "fi-FI",
    "sv-SE",
    "hu-HU",
  ]
  const lunarLocalesU = [
    "en-US",
    "en-CA",
    "fr-CA",
    "fr-FR",
    "de-DE",
    "en-GB",
    // "nl-BE",
    // "fr-BE",
    // "en-IE",
    // "it-IT",
    // "nl-NL",
    // "de-AT",
    // "sv-SE",
    "en-AU",
    "en-NZ",
    "ja-JP",
  ]
  const moonStonesLocales = [
    "en-US",
    "zh-TW",
    "zh-HK",
    "en-CA",
    "ja-JP",
    "fr-CA",
    "de-DE",
    "fr-FR",
    "en-GB",
    "en-AU",
    "en-NZ",
    "de-AT",
    "de-CH",
    "fr-CH",
    "it-CH",
    "da-DK",
    "en-HK",
    "zh-HK",
    "zh-TW",
    "en-IE",
    "es-ES",
    "nl-BE",
    "nl-NL",
    "fr-BE",
    "fr-LU",
    "it-IT",
    "nb-NO",
    "pl-PL",
    "fi-FI",
    "sv-SE",
    "hu-HU",
  ]

  const carltonRelease = [
    "en-US",
    "en-CA",
    "fr-CA",
    // "fr-FR",
    // "de-DE",
    // "en-GB",
    "nl-BE",
    "fr-BE",
    "en-IE",
    "it-IT",
    "de-AT",
    "nl-nL",
    "fr-FR",
    "de-DE",
    "en-GB",
    "de-CH",
    "fr-CH",
    "it-CH",
    "fi-FI",
    "da-DK",
    "nb-NO",
    "pl-PL",
    "sv-SE",
    "es-ES",
    "fr-LU",
    "hu-HU",
    "zh-HK",
    "en-HK",
    "zh-TW",
    "ja-JP",

    // "nl-NL",
    // "de-AT",
    // "sv-SE",
    "en-AU",
    "en-NZ",
    // "ja-JP",
  ]
  const citrusLocalesFlex = [
    "en-US",
    "en-CA",
    "en-AU",
    "en-NZ",
    "fr-CA",
    "en-GB",
    "fr-FR",
    "de-DE",
    "ja-JP",
    "nl-BE",
    "fr-BE",
    "en-IE",
    "it-IT",
    "nl-NL",
    "de-AT",
    "fi-FI",
    "fr-CH",
    "de-CH",
    "it-CH",
    "da-DK",
    "nb-NO",
    "sv-SE",
    "pl-PL",
    "zh-HK",
    "en-HK",
    "zh-TW",
  ]
  const twilightLocales = ["en-US", "en-CA", "en-GB", "fr-FR", "de-DE", "fr-CA", "en-AU", "en-NZ", "zh-HK", "zh-TW", "ja-JP", "en-HK"]

  const carltonReleaseSand = [
    "en-US",
    "en-CA",
    "fr-CA",
    "nl-BE",
    "fr-BE",
    "en-IE",
    "it-IT",
    "de-AT",
    "nl-nL",
    "de-CH",
    "fr-CH",
    "it-CH",
    "fi-FI",
    "da-DK",
    "nb-NO",
    "pl-PL",
    "sv-SE",
    "es-ES",
    "fr-LU",
    "hu-HU",
    "zh-HK",
    "en-HK",
    "zh-TW",
    "ja-JP",
    "fr-FR",
    "de-DE",
    "en-GB",
    // "nl-BE",
    // "fr-BE",
    // "en-IE",
    // "it-IT",
    // "nl-NL",
    // "de-AT",
    // "sv-SE",
    "en-AU",
    "en-NZ",
    // "ja-JP",
  ]

  const qcueMidnight = ["en-US", "en-CA", "fr-CA", "en-GB", "fr-FR", "de-DE", "en-AU", "en-NZ", "ja-JP"]
  const qcueDesert = [
    "en-US",
    "en-CA",
    "fr-CA",
    "en-GB",
    "fr-FR",
    "de-DE",
    "en-AU",
    "en-NZ",
    "de-AT",
    "de-CH",
    "it-CH",
    "fr-CH",
    "it-IT",
    "fr-BE",
    "nl-BE",
    "en-IE",
    "nl-NL",
    "fi-FI",
    "da-DK",
    "nb-NO",
    "pl-PL",
    "sv-SE",
    "es-ES",
    "fr-LU",
  ]

  const vivianMoonlight = ["en-US", "en-CA", "fr-CA", "fr-FR", "de-DE", "en-GB"]
  const qcheadphonesCypress = ["en-US", "en-CA", "fr-CA"]

  const wolverineGold = ["en-US", "en-CA", "fr-CA", "en-GB", "fr-FR", "de-DE", "en-AU", "en-NZ", "zh-HK", "en-HK", "zh-TW"]

  const vivian = ["en-US", "en-CA", "fr-CA", "de-DE", "fr-FR", "ja-JP", "en-GB", "fi-FI", "da-DK", "nb-NO", "sv-SE"]
  const vivianPink = ["en-US", "en-CA", "fr-CA", "ja-JP"]
  const uoebNew = ["en-US", "en-CA", "fr-CA", "de-DE", "fr-FR", "en-GB", "en-AU", "en-NZ"]
  const qceSprout = ["en-US", "en-CA", "fr-CA", "fr-FR", "de-DE", "en-GB"]

  const uoebDriftWoodViolet = ["en-US", "en-CA", "fr-CA", "en-GB", "fr-FR", "de-DE", "en-AU", "en-NZ", "ja-JP", "zh-HK", "en-HK", "zh-TW"]
  const uoebMoon = ["en-US", "en-CA", "fr-CA", "en-AU", "en-NZ", "fr-FR", "de-DE", "en-GB"]
  const uoebGold = [
    "en-US",
    "en-CA",
    "fr-CA",
    "en-GB",
    "fr-FR",
    "de-DE",
    "en-AU",
    "en-NZ",
    "de-AT",
    "de-CH",
    "it-CH",
    "fr-CH",
    "it-IT",
    "fr-BE",
    "nl-BE",
    "en-IE",
    "nl-NL",
    "fi-FI",
    "da-DK",
    "nb-NO",
    "pl-PL",
    "sv-SE",
    "es-ES",
    "fr-LU",
  ]

  const billyPink = [  "en-US",
        "en-CA",
        "fr-CA",
        "en-AU",
        "en-NZ",
        "en-GB",
        "fr-FR",
        "de-DE",
        "ja-JP",
        ];

    const billySand = [
    "en-US",
    "en-CA",
    "fr-CA",
    "en-GB",
    // "en-AU",
    // "en-NZ",
    "fr-FR",
    "de-DE",
    // "ja-JP",
    // "nl-BE",
    // "fr-BE",
    // "en-IE",
    // "it-IT",
    // "nl-NL",
    // "de-AT",
    // "fi-FI",
    // "fr-CH",
    // "de-CH",
    // "it-CH",
    // "da-DK",
    // "nb-NO",
    // "sv-SE",
    // "pl-PL",
  ]

  const billyBlue = [ "en-US",
        "en-CA",
        "fr-CA",
        "en-AU",
        "en-GB",
        "fr-FR",
        "de-DE",
        "ja-JP",
        "en-NZ" ]

  useEffect(() => {
    if (product === "qcheadphones") {
      const colorOptions = document.querySelectorAll(".colorOptions")
      const len = colorOptions[0].children.length

      if (len >= 3) {
        // colorOptions[0].children[len - 1].style.display = "none" // Last child
        // colorOptions[0].children[len - 2].style.display = "none" // Second last child
        // colorOptions[0].children[len - 3].style.display = "none" // Third last child
      }
      if (carltonRelease.includes(i18n.language)) {
        // Apply display:none to the fifth and seventh child
        if (colorOptions[0].children[4]) {
          colorOptions[0].children[4].style.display = "block" // Fifth child
        }
        if (colorOptions[0].children[5]) {
          colorOptions[0].children[5].style.display = "block" // Fifth child
        }
        if (colorOptions[0].children[6]) {
          colorOptions[0].children[6].style.display = "none" // Seventh child
        }
      }

      if (carltonRelease.includes(i18n.language) && carltonReleaseSand.includes(i18n.language)) {
        // Apply display:none to the fifth and seventh child
        if (colorOptions[0].children[4]) {
          colorOptions[0].children[4].style.display = "block" // Fifth child
        }
        if (colorOptions[0].children[5]) {
          colorOptions[0].children[5].style.display = "block" // Fifth child
        }
        if (colorOptions[0].children[6]) {
          colorOptions[0].children[6].style.display = "block" // Seventh child
        }
      }
      // Apply display:none to the last three children
    }
  }, [product, i18n.language])

  useEffect(() => {
    if (product === "qcue2") {
      const colorOptions = document.querySelectorAll(".colorOptions")
      if (!qcueMidnight.includes(i18n.language)) {
        ;["Midnight"].forEach((id) => {
          const element = Array.from(colorOptions[0].children).find((child) => child.id === id)
          if (element) {
            element.style.display = "none"
          }
        })
      }

      if (!qcueDesert.includes(i18n.language)) {
        ;["Desert Gold"].forEach((id) => {
          const element = Array.from(colorOptions[0].children).find((child) => child.id === id)
          if (element) {
            element.style.display = "none"
          }
        })
      }

      const reorderIds = ["Midnight", "Desert Gold", "White", "Deep Plum", "Black"]
      const reorderedElements = reorderIds
        .map((id) => Array.from(colorOptions[0].children).find((child) => child.id === id))
        .filter(Boolean) // Filter out any nulls in case of missing elements

      // Append reordered elements to the container
      reorderedElements.forEach((element) => {
        colorOptions[0].appendChild(element) // Move each element to the end in the new order
      })
    }

    if (product === "uoeb") {
      const colorOptions = document.querySelectorAll(".colorOptions")

      // Existing code for hiding elements based on locale
      if (!diamondLocales.includes(i18n.language)) {
        if (colorOptions[0].children[2]) {
          colorOptions[0].children[2].style.display = "none" // Fifth child
        }
        if (colorOptions[0].children[3]) {
          colorOptions[0].children[3].style.display = "none" // Seventh child
        }
      } else {
        colorOptions[0].children[2].style.display = "none" // Fifth child
        colorOptions[0].children[3].style.display = "block" // Seventh child
      }

      if (!lunarLocalesU.includes(i18n.language)) {
        colorOptions[0].children[4].style.display = "none"
      }
      if (moonStonesLocales.includes(i18n.language)) {
        colorOptions[0].children[2].style.display = "block"
      }

      if (!uoebNew.includes(i18n.language)) {
        ;["Carbon", "Sunset", "Lilac", "Sand"].forEach((id) => {
          const element = Array.from(colorOptions[0].children).find((child) => child.id === id)
          if (element) {
            element.style.display = "none"
          }
        })
      }
      if (!uoebDriftWoodViolet.includes(i18n.language)) {
        ;["Driftwood", "Midnight"].forEach((id) => {
          const element = Array.from(colorOptions[0].children).find((child) => child.id === id)
          if (element) {
            element.style.display = "none"
          }
        })
      }
      if (!uoebGold.includes(i18n.language)) {
        ;["Desert"].forEach((id) => {
          const element = Array.from(colorOptions[0].children).find((child) => child.id === id)
          if (element) {
            element.style.display = "none"
          }
        })
      }
      if (!uoebMoon.includes(i18n.language)) {
        ;["Moonlight"].forEach((id) => {
          const element = Array.from(colorOptions[0].children).find((child) => child.id === id)
          if (element) {
            element.style.display = "none"
          }
        })
      }

      if (["en-NZ", "en-AU"].includes(i18n.language)) {
        ;["Carbon", "Sand"].forEach((id) => {
          const element = Array.from(colorOptions[0].children).find((child) => child.id === id)
          if (element) {
            element.style.display = "none"
          }
        })
      }
      if (!["en-US", "en-CA", "fr-CA", "en-GB", "de-DE", "fr-FR", "ja-JP", "en-AU", "en-NZ"].includes(i18n.language)) {
        ;["Purple"].forEach((id) => {
          const element = Array.from(colorOptions[0].children).find((child) => child.id === id)
          if (element) {
            element.style.display = "none"
          }
        })
      }
      if (!["xx"].includes("xx-xx")) {
        ;["Carbon"].forEach((id) => {
          const element = Array.from(colorOptions[0].children).find((child) => child.id === id)
          if (element) {
            element.style.display = "none"
          }
        })
      }

      // Reorder elements by ID after all visibility changes
      const reorderIds = [
        "Black",
        "White",
        "Diamond",
        "Sunset",
        "Midnight",
        "Purple",
        "Carbon",
        "Lilac",
        "Sand",
        "Moonlight",
        "Lunar",
        "Driftwood",
        "Blue",
        "Desert",
      ]
      const reorderedElements = reorderIds
        .map((id) => Array.from(colorOptions[0].children).find((child) => child.id === id))
        .filter(Boolean) // Filter out any nulls in case of missing elements

      // Append reordered elements to the container
      reorderedElements.forEach((element) => {
        colorOptions[0].appendChild(element) // Move each element to the end in the new order
      })
    }
  }, [product, i18n.language])

  useEffect(() => {
    if (product === "qcuhp" || product === "qcueb") {
      const colorOptions = document.querySelectorAll(".colorOptions")

      if (
        ![
          "en-US",
          "en-CA",
          "fr-CA",
          "fr-FR",
          "en-GB",
          "de-DE",
          "nl-BE",
          "nl-NL",
          "fr-BE",
          "en-IE",
          "it-IT",
          "de-AT",
          "da-DK",
          "nb-NO",
          "fi-FI",
          "pl-PL",
          "de-CH",
          "fr-CH",
          "it-CH",
          "sv-SE",
          "ja-JP",
          "en-AU",
          "en-NZ",
        ].includes(i18n.language)
      ) {
        ;["Deep Plum"].forEach((id) => {
          const element = Array.from(colorOptions[0].children).find((child) => child.id === id)
          if (element) {
            element.style.display = "none"
          }
        })
      }

      if (!diamondLocales.includes(i18n.language)) {
        // Apply display:none to the fifth and seventh child
        if (colorOptions[0].children[3]) {
          colorOptions[0].children[3].style.display = "none" // Fifth child
        }
        if (colorOptions[0].children[4]) {
          colorOptions[0].children[4].style.display = "none" // Seventh child
        }
      } else {
        colorOptions[0].children[3].style.display = "none" // Fifth child
        colorOptions[0].children[4].style.display = "block" // Seventh child
        // if (len >= 3) {
        //   // colorOptions[0].children[len - 1].style.display = "none" // Last child
        //   colorOptions[0].children[len - 2].style.display = "none" // Second last child
        //   // colorOptions[0].children[len - 3].style.display = "none" // Third last child
        // }
      }
      if (lunarLocales.includes(i18n.language)) {
        colorOptions[0].children[3].style.display = "block" // Seventh child
      }
    }

    // if (product === "flex2") {
    //   const colorOptions = document.querySelectorAll(".colorOptions")
    //   if (!twilightLocales.includes(i18n.language)) {
    //     ;["Twighlight Blue"].forEach((id) => {
    //       const element = Array.from(colorOptions[0].children).find((child) => child.id === id)
    //       if (element) {
    //         element.style.display = "none"
    //       }
    //     })
    //     if (!citrusLocalesFlex.includes(i18n.language)) {
    //       ;["Citrus Yellow"].forEach((id) => {
    //         const element = Array.from(colorOptions[0].children).find((child) => child.id === id)
    //         if (element) {
    //           element.style.display = "none"
    //         }
    //       })
    //     }
    //   }
    // }

    if (product === "qce" || product === "qcheadphones") {
      const colorOptions = document.querySelectorAll(".colorOptions")

      if (!qceSprout.includes(i18n.language)) {
        ;["Ice Blue"].forEach((id) => {
          const element = Array.from(colorOptions[0].children).find((child) => child.id === id)
          if (element) {
            element.style.display = "none"
          }
        })
      }

    }

        if (product === "slmicro2") {

                const peachLocales = [
                "en-US",
                "en-CA",
                "fr-CA",
                "de-DE",
                "fr-FR",
                "en-GB",
                "ja-JP",
                "en-AU",
                "en-NZ"
  ]
         
      const colorOptions = document.querySelectorAll(".colorOptions")

             if (!peachLocales.includes(i18n.language)) {
        ;["sunset peach"].forEach((id) => {
          const element = Array.from(colorOptions[0].children).find((child) => child.id === id)
          if (element) {
            element.style.display = "none"
          }
        })
      }
   
      if (!billyBlue.includes(i18n.language)) {
        ;["blue"].forEach((id) => {
          const element = Array.from(colorOptions[0].children).find((child) => child.id === id)
          if (element) {
            element.style.display = "none"
          }
        })
      }
        if (!billyPink.includes(i18n.language)) {
          ;["petal"].forEach((id) => {
            const element = Array.from(colorOptions[0].children).find((child) => child.id === id)
            if (element) {
              element.style.display = "none"
            }
          })
        }
            if (!billySand.includes(i18n.language)) {
              
          ;["sand"].forEach((id) => {
            const element = Array.from(colorOptions[0].children).find((child) => child.id === id)
            if (element) {
              element.style.display = "none"
            }
          })
        }
      
    }

    if (product === "qce" || product === "qcheadphones") {
      const colorOptions = document.querySelectorAll(".colorOptions")


      if (!vivianPink.includes(i18n.language)) {
        ;["Petal Pink"].forEach((id) => {
          const element = Array.from(colorOptions[0].children).find((child) => child.id === id)
          if (element) {
            element.style.display = "none"
          }
        })
      }
      if (!vivian.includes(i18n.language)) {
        ;["Twilight"].forEach((id) => {
          const element = Array.from(colorOptions[0].children).find((child) => child.id === id)
          if (element) {
            element.style.display = "none"
          }
        })
      }
      if (product === "qcheadphones") {
        if (!vivianMoonlight.includes(i18n.language)) {
          ;["Moonlight Gray"].forEach((id) => {
            const element = Array.from(colorOptions[0].children).find((child) => child.id === id)
            if (element) {
              element.style.display = "none"
            }
          })
        }
        const reorderIds = [
          "Black",
          "Moonlight Gray",
          "White",
          "Dusk",
          "Blue",
          "Cypress Green",
          "Twilight",
          "Ice blue",
          "Sand",
          "Petal Pink",
          "Purple",
        ]
        const reorderedElements = reorderIds
          .map((id) => Array.from(colorOptions[0].children).find((child) => child.id === id))
          .filter(Boolean) // Filter out any nulls in case of missing elements

        // Append reordered elements to the container
        reorderedElements.forEach((element) => {
          colorOptions[0].appendChild(element) // Move each element to the end in the new order
        })
      }
    }
    // REMOVE UNUSED COLS
    if (product === "qcuhp") {
      const colorOptions = document.querySelectorAll(".colorOptions")

      if (!["xx"].includes(i18n.language)) {
        ;["Sandstone", "Diamond"].forEach((id) => {
          const element = Array.from(colorOptions[0].children).find((child) => child.id === id)
          if (element) {
            element.style.display = "none"
          }
        })
      }
    }

    if (product === "qcuhp2") {
      const colorOptions = document.querySelectorAll(".colorOptions")

      if (!wolverineGold.includes(i18n.language)) {
        ;["Desert"].forEach((id) => {
          const element = Array.from(colorOptions[0].children).find((child) => child.id === id)
          if (element) {
            element.style.display = "none"
          }
        })
      }
      const reorderIds = ["Black", "Desert", "Violet", "Sandstone", "White", "Cherry Chocolate", "Olive Green"]
      const reorderedElements = reorderIds
        .map((id) => Array.from(colorOptions[0].children).find((child) => child.id === id))
        .filter(Boolean) // Filter out any nulls in case of missing elements

      // Append reordered elements to the container
      reorderedElements.forEach((element) => {
        colorOptions[0].appendChild(element) // Move each element to the end in the new order
      })
    }

    if (product === "uoeb") {
      const colorOptions = document.querySelectorAll(".colorOptions")

      if (!["xx"].includes(i18n.language)) {
        ;["Sand", "Diamond"].forEach((id) => {
          const element = Array.from(colorOptions[0].children).find((child) => child.id === id)
          if (element) {
            element.style.display = "none"
          }
        })
      }
    }

               const peachLocales = [
                "en-US",
                "en-CA",
                "fr-CA",
                "de-DE",
                "fr-FR",
                "en-GB",
                "ja-JP",
                "en-AU",
                "en-NZ"
  ]

    if (product === "slmicro2") {
     
      const colorOptions = document.querySelectorAll(".colorOptions")


    }
        if (product === "slplus") {
      const colorOptions = document.querySelectorAll(".colorOptions")

       if (!peachLocales.includes(i18n.language)) {
        ;["Sunset Peach"].forEach((id) => {
          const element = Array.from(colorOptions[0].children).find((child) => child.id === id)
          if (element) {
            element.style.display = "none"
          }
        })
      }
    }
     if (product === "flex2") {
      const colorOptions = document.querySelectorAll(".colorOptions")

       if (!peachLocales.includes(i18n.language)) {
        ;["Sunset Peach"].forEach((id) => {
          const element = Array.from(colorOptions[0].children).find((child) => child.id === id)
          if (element) {
            element.style.display = "none"
          }
        })
      }

      if (!iceFlex.includes(i18n.language)) {
        ;["Fog Grey"].forEach((id) => {
          const element = Array.from(colorOptions[0].children).find((child) => child.id === id)
          if (element) {
            element.style.display = "none"
          }
        })
      }
    }

    if (product === "qcueb") {
      const colorOptions = document.querySelectorAll(".colorOptions")

      if (!["xx"].includes(i18n.language)) {
        ;["Blue"].forEach((id) => {
          const element = Array.from(colorOptions[0].children).find((child) => child.id === id)
          if (element) {
            element.style.display = "none"
          }
        })
      }
    }
    // if (product === "slmicro2") {
    //   const colorOptions = document.querySelectorAll(".colorOptions")
    //   if (!billySand.includes(i18n.language)) {
    //     ;["sand"].forEach((id) => {
    //       const element = Array.from(colorOptions[0].children).find((child) => child.id === id)
    //       if (element) {
    //         element.style.display = "none"
    //       }
    //     })
    //   }
    //   if (!billyPetal.includes(i18n.language)) {
    //     ;["petal", "twilight"].forEach((id) => {
    //       const element = Array.from(colorOptions[0].children).find((child) => child.id === id)
    //       if (element) {
    //         element.style.display = "none"
    //       }
    //     })
    //   }
    // }

    if (product === "qcheadphones") {
      const colorOptions = document.querySelectorAll(".colorOptions")
      if (!["xx"].includes(i18n.language)) {
        ;["Blue"].forEach((id) => {
          const element = Array.from(colorOptions[0].children).find((child) => child.id === id)
          if (element) {
            element.style.display = "none"
          }
        })
      }
      if (!qcheadphonesCypress.includes(i18n.language)) {
        ;["Cypress Green"].forEach((id) => {
          const element = Array.from(colorOptions[0].children).find((child) => child.id === id)
          if (element) {
            element.style.display = "none"
          }
        })
      }

      if (!qcheadphoneIce.includes(i18n.language)) {
        ;["Ice blue"].forEach((id) => {
          const element = Array.from(colorOptions[0].children).find((child) => child.id === id)
          if (element) {
            element.style.display = "none"
          }
        })
      }
    }
  }, [product, i18n.language])

  const juiceboxLocales = [
    "en-US",
    "en-CA",
    "fr-CA",
    "en-GB",
    "en-AU",
    "en-NZ",
    "fr-FR",
    "de-DE",
    "ja-JP",
    "nl-BE",
    "fr-BE",
    "en-IE",
    "it-IT",
    "nl-NL",
    "de-AT",
    "fi-FI",
    "fr-CH",
    "de-CH",
    "it-CH",
    "da-DK",
    "nb-NO",
    "sv-SE",
    "pl-PL",
  ]




  const citrusLocales = [
    "en-US",
    "en-CA",
    "en-GB",
    "it-IT",
    "nl-NL",
    "de-AT",
    "sv-SE",
    "pl-PL",
    "fr-CA",
    "de-DE",
    "fr-FR",
    "fr-BE",
    "nl-BE",
    "en-IE",
    "de-CH",
    "fr-CH",
    "ja-JP",
    "it-CH",
    "fi-FI",
    "da-DK",
    "nb-NO",
    "es-ES",
    "fr-LU",
    "zh-HK",
    "en-HK",
    "en-AU",
    "en-NZ",
    "zh-TW",
  ]

 

  const warmWoodLocales = [
    "ja-JP",
    "en-AU",
    "en-NZ",
    "en-US",
    "en-CA",
    "fr-CA",
    "deAT",
    "deCH",
    "frCH",
    "itCH",
    "nlBE",
    "frBE",
    "enIE",
    "itIT",
    "nlNL",
    "fiFI",
    "daDK",
    "nbNO",
    "plPL",
    "svSE",
    "esES",
    "frLU",
  ]

  const newColorRestrictedLocales = ["en-AU", "en-NZ"]
  const restrictedNewColors = {
    qce: ["Dewdrop Mint", "Rosewood Mauve"],
    flex2: ["Dewdrop Mint", "Rosewood Mauve"],
    slmicro2: ["dewdrop mint"],
  }

  const getColorItems = () => {
    if ((product === "qce2" && noPorterLocales.includes(i18n.language)) || (product === "qc45" && noQC45Locales.includes(i18n.language))) {
      const remove = [2, 3]
      const indexSet = new Set(remove)
      const array = config.colorNames
      const filteredColorNames = array.filter((value, i) => !indexSet.has(i))
      return filteredColorNames
    }
    // if (product === "flex2" && !iceFlex.includes(i18n.language)) {
    //   const remove = [7]
    //   const indexSet = new Set(remove)
    //   const array = config?.colorNames
    //   const filteredColorNames = array.filter((value, i) => !indexSet.has(i))
    //   return filteredColorNames
    // }

    if (product === "slmax" && !juiceboxLocales.includes(i18n.language)) {
      const remove = [2]
      const indexSet = new Set(remove)
      const array = config?.colorNames
      const filteredColorNames = array.filter((value, i) => !indexSet.has(i))
      return filteredColorNames
    }

    // if (product === "slmicro2" && !billySand.includes(i18n.language)) {
    //   const remove = [2, 3, 4]
    //   const indexSet = new Set(remove)
    //   const array = config?.colorNames
    //   const filteredColorNames = array.filter((value, i) => !indexSet.has(i))
    //   return filteredColorNames
    // }

    if (product === "qcuhp" && noSandStoneLocales.includes(i18n.language)) {
      const remove = [2]
      const indexSet = new Set(remove)
      const array = config.colorNames
      const filteredColorNames = array.filter((value, i) => !indexSet.has(i))
      return filteredColorNames
    }
    if (product === "slplus" && !citrusLocales.includes(i18n.language)) {
      const remove = [2]
      const indexSet = new Set(remove)
      const array = config?.colorNames
      const filteredColorNames = array.filter((value, i) => !indexSet.has(i))
      return filteredColorNames
    }
    if (product === "flex" && !flexLocales.includes(i18n.language)) {
      const remove = [4]
      const indexSet = new Set(remove)
      const array = config.colorNames
      const filteredColorNames = array.filter((value, i) => !indexSet.has(i))
      return filteredColorNames
    }

    if (product === "slhome" && !warmWoodLocales.includes(i18n.language)) {
      const remove = [2] //hide warm wood
      const indexSet = new Set(remove)
      const array = config?.colorNames
      const filteredColorNames = array.filter((value, i) => !indexSet.has(i))
      return filteredColorNames
    }

    return config.colorNames
  }
  const qch2HiddenColors = ["Vanilla Cream & Ice Blue"]
  const qch2SkyPinkLocales = ["en-US", "en-CA", "fr-CA", "en-GB", "fr-FR", "de-DE", "ja-JP", "en-AU", "en-NZ"]
  const qcweHiddenColors = ["Orchid Pink", "Soft Lilac"]
  const oseHiddenColors = ["Coastal Blue", "Vanilla Cream"]
  const qcuhp2NewColors = ["Cherry Chocolate", "Olive Green"]
  const qcuhp2NewColorLocales = ["en-CA", "fr-CA", "en-US", "en-GB", "fr-FR", "de-DE", "ja-JP", "en-AU", "en-NZ"]
  const colorList = getColorItems().map((currElement, index) => (
    <div
      key={index}
      aria-label={`Switch color to ${config.colorNames[index]}`}
      id={config.colorNames[index]}
      tabIndex={0}
      onClick={() => {
        idleModel(controls.current.target)
        setColor(index)
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          idleModel(controls.current.target)
          setColor(index)
        }
      }}
      className={"colorOuter"}
      style={{
        display:
          hide.includes(index + 1) ||
          (product === "qcuhp2" && qcuhp2NewColors.includes(currElement) && !qcuhp2NewColorLocales.includes(i18n.language)) ||
          (product === "qch2" && qch2HiddenColors.includes(currElement)) ||
          (product === "qch2" && currElement === "Sky Pink" && !qch2SkyPinkLocales.includes(i18n.language)) ||
          (product === "qcwe" && qcweHiddenColors.includes(currElement)) ||
          (product === "ose" && oseHiddenColors.includes(currElement)) ||
          (newColorRestrictedLocales.includes(i18n.language) && restrictedNewColors[product]?.includes(currElement))
            ? "none"
            : "block",
        border: color == index ? "solid 1px rgb(41, 41, 41)" : "solid 1px transparent",
      }}>
      <div style={{ backgroundColor: config.colorValues[index] }} className={"colorInner"}></div>
    </div>
  ))
  const getDpr = () => {
    if (devMode) {
      return isMobile ? renderSettings.dprMobile : renderSettings.dprDesktop
    }

    if (!isMobile) {
      return catalina ? 1 : 1
    } else return 2
  }
  const renderHandOverlay = () => {
    if (loaded) {
      return (
        <div className={"handInstruction"} style={{ opacity: loaded ? "1" : "none" }}>
          <svg viewBox="0 0 25 36" xmlns="http://www.w3.org/2000/svg">
            <g transform="translate(-11 -4)" fill="none" fillRule="evenodd">
              <path d="M0 0h44v44H0z" fill="#fff" fillOpacity="0" />
              <g transform="translate(11 3)">
                <path
                  d="M8.733 11.165c.04-1.108.766-2.027 1.743-2.307a2.54 2.54 0 0 1 .628-.089c.16 0 .314.017.463.044 1.088.2 1.9 1.092 1.9 2.16v8.88h1.26c2.943-1.39 5-4.45 5-8.025a9.01 9.01 0 0 0-1.9-5.56l-.43-.5c-.765-.838-1.683-1.522-2.712-2-1.057-.49-2.226-.77-3.46-.77s-2.4.278-3.46.77c-1.03.478-1.947 1.162-2.71 2l-.43.5a9.01 9.01 0 0 0-1.9 5.56 9.04 9.04 0 0 0 .094 1.305c.03.21.088.41.13.617l.136.624c.083.286.196.56.305.832l.124.333a8.78 8.78 0 0 0 .509.953l.065.122a8.69 8.69 0 0 0 3.521 3.191l1.11.537v-9.178z"
                  fill="#e4e4e4"
                  fillOpacity=".5"
                />
                <path
                  d="M22.94 26.218l-2.76 7.74c-.172.485-.676.8-1.253.8H12.24c-1.606 0-3.092-.68-3.98-1.82-1.592-2.048-3.647-3.822-6.11-5.27-.095-.055-.15-.137-.152-.23-.004-.1.046-.196.193-.297.56-.393 1.234-.6 1.926-.6a3.43 3.43 0 0 1 .691.069l4.922.994V10.972c0-.663.615-1.203 1.37-1.203s1.373.54 1.373 1.203v9.882h2.953c.273 0 .533.073.757.21l6.257 3.874c.027.017.045.042.07.06.41.296.586.77.426 1.22M4.1 16.614c-.024-.04-.042-.083-.065-.122a8.69 8.69 0 0 1-.509-.953c-.048-.107-.08-.223-.124-.333l-.305-.832c-.058-.202-.09-.416-.136-.624l-.13-.617a9.03 9.03 0 0 1-.094-1.305c0-2.107.714-4.04 1.9-5.56l.43-.5c.764-.84 1.682-1.523 2.71-2 1.058-.49 2.226-.77 3.46-.77s2.402.28 3.46.77c1.03.477 1.947 1.16 2.712 2l.428.5a9 9 0 0 1 1.901 5.559c0 3.577-2.056 6.636-5 8.026h-1.26v-8.882c0-1.067-.822-1.96-1.9-2.16-.15-.028-.304-.044-.463-.044-.22 0-.427.037-.628.09-.977.28-1.703 1.198-1.743 2.306v9.178l-1.11-.537C6.18 19.098 4.96 18 4.1 16.614M22.97 24.09l-6.256-3.874c-.102-.063-.218-.098-.33-.144 2.683-1.8 4.354-4.855 4.354-8.243 0-.486-.037-.964-.104-1.43a9.97 9.97 0 0 0-1.57-4.128l-.295-.408-.066-.092a10.05 10.05 0 0 0-.949-1.078c-.342-.334-.708-.643-1.094-.922-1.155-.834-2.492-1.412-3.94-1.65l-.732-.088-.748-.03a9.29 9.29 0 0 0-1.482.119c-1.447.238-2.786.816-3.94 1.65a9.33 9.33 0 0 0-.813.686 9.59 9.59 0 0 0-.845.877l-.385.437-.36.5-.288.468-.418.778-.04.09c-.593 1.28-.93 2.71-.93 4.222 0 3.832 2.182 7.342 5.56 8.938l1.437.68v4.946L5 25.64a4.44 4.44 0 0 0-.888-.086c-.017 0-.034.003-.05.003-.252.004-.503.033-.75.08a5.08 5.08 0 0 0-.237.056c-.193.046-.382.107-.568.18-.075.03-.15.057-.225.1-.25.114-.494.244-.723.405a1.31 1.31 0 0 0-.566 1.122 1.28 1.28 0 0 0 .645 1.051C4 29.925 5.96 31.614 7.473 33.563a5.06 5.06 0 0 0 .434.491c1.086 1.082 2.656 1.713 4.326 1.715h6.697c.748-.001 1.43-.333 1.858-.872.142-.18.256-.38.336-.602l2.757-7.74c.094-.26.13-.53.112-.794s-.088-.52-.203-.76a2.19 2.19 0 0 0-.821-.91"
                  fill="#000"
                  fillOpacity=".6"
                />
                <path
                  d="M22.444 24.94l-6.257-3.874a1.45 1.45 0 0 0-.757-.211h-2.953v-9.88c0-.663-.616-1.203-1.373-1.203s-1.37.54-1.37 1.203v16.643l-4.922-.994a3.44 3.44 0 0 0-.692-.069 3.35 3.35 0 0 0-1.925.598c-.147.102-.198.198-.194.298.004.094.058.176.153.23 2.462 1.448 4.517 3.22 6.11 5.27.887 1.14 2.373 1.82 3.98 1.82h6.686c.577 0 1.08-.326 1.253-.8l2.76-7.74c.16-.448-.017-.923-.426-1.22-.025-.02-.043-.043-.07-.06z"
                  fill="#fff"
                />
                <g transform="translate(0 .769)">
                  <mask id="a" fill="#fff" />
                  <path
                    d="M23.993 24.992a1.96 1.96 0 0 1-.111.794l-2.758 7.74c-.08.22-.194.423-.336.602-.427.54-1.11.87-1.857.872h-6.698c-1.67-.002-3.24-.633-4.326-1.715-.154-.154-.3-.318-.434-.49C5.96 30.846 4 29.157 1.646 27.773c-.385-.225-.626-.618-.645-1.05a1.31 1.31 0 0 1 .566-1.122 4.56 4.56 0 0 1 .723-.405l.225-.1a4.3 4.3 0 0 1 .568-.18l.237-.056c.248-.046.5-.075.75-.08.018 0 .034-.003.05-.003.303-.001.597.027.89.086l3.722.752V20.68l-1.436-.68c-3.377-1.596-5.56-5.106-5.56-8.938 0-1.51.336-2.94.93-4.222.015-.03.025-.06.04-.09.127-.267.268-.525.418-.778.093-.16.186-.316.288-.468.063-.095.133-.186.2-.277L3.773 5c.118-.155.26-.29.385-.437.266-.3.544-.604.845-.877a9.33 9.33 0 0 1 .813-.686C6.97 2.167 8.31 1.59 9.757 1.35a9.27 9.27 0 0 1 1.481-.119 8.82 8.82 0 0 1 .748.031c.247.02.49.05.733.088 1.448.238 2.786.816 3.94 1.65.387.28.752.588 1.094.922a9.94 9.94 0 0 1 .949 1.078l.066.092c.102.133.203.268.295.408a9.97 9.97 0 0 1 1.571 4.128c.066.467.103.945.103 1.43 0 3.388-1.67 6.453-4.353 8.243.11.046.227.08.33.144l6.256 3.874c.37.23.645.55.82.9.115.24.185.498.203.76m.697-1.195c-.265-.55-.677-1.007-1.194-1.326l-5.323-3.297c2.255-2.037 3.564-4.97 3.564-8.114 0-2.19-.637-4.304-1.84-6.114-.126-.188-.26-.37-.4-.552-.645-.848-1.402-1.6-2.252-2.204C15.472.91 13.393.232 11.238.232A10.21 10.21 0 0 0 5.23 2.19c-.848.614-1.606 1.356-2.253 2.205-.136.18-.272.363-.398.55C1.374 6.756.737 8.87.737 11.06c0 4.218 2.407 8.08 6.133 9.842l.863.41v3.092l-2.525-.51c-.356-.07-.717-.106-1.076-.106a5.45 5.45 0 0 0-3.14.996c-.653.46-1.022 1.202-.99 1.983a2.28 2.28 0 0 0 1.138 1.872c2.24 1.318 4.106 2.923 5.543 4.772 1.26 1.62 3.333 2.59 5.55 2.592h6.698c1.42-.001 2.68-.86 3.134-2.138l2.76-7.74c.272-.757.224-1.584-.134-2.325"
                    fill="#000"
                    mask="url(#a)"
                    fillOpacity=".05"
                  />
                </g>
              </g>
            </g>
          </svg>
        </div>
      )
    }
  }
  useEffect(() => {
    if (locale) {
      i18n.changeLanguage(locale)
    }
  }, [analytics, locale, r])

  const [currentLocale, setCurrentLocale] = useState("en-US")
  const localeLang = `${currentLocale}`

  useEffect(() => {
    setCurrentLocale(`${window.location.pathname}`)
  }, [])

  const getFontFamily = (fontLocale) => {
    switch (fontLocale) {
      case "/ja-JP":
        return `BoseText-JP`
      case "/zh-HK":
        return `BoseText-ZH`
      case "/zh-TW":
        return `BoseText-ZH`
      default:
        return `BoseText`
    }
  }

  const updateGen2MaterialColor = (colorName, materialName, value) => {
    gen2MaterialColorOptionsDraft.current = {
      ...gen2MaterialColorOptionsDraft.current,
      [colorName]: {
        ...(gen2MaterialColorOptionsDraft.current[colorName] || {}),
        [materialName]: value,
      },
    }

    if (colorName !== config.colorNames[color]) return

    const materials = gen2LiveMaterials.current[materialName]
    if (!materials) return

    const targetColor = new THREE.Color(normalizeHexColor(value))
    materials.forEach((material) => {
      material.color.copy(targetColor)
      material.needsUpdate = true
    })

    gen2MaterialSnapshot[materialName] = getMaterialSnapshot(Array.from(materials)[0])
  }

  const resetGen2MaterialColor = (colorName, materialName) => {
    const originalValue = config.materialColorOptions?.[colorName]?.[materialName]
    if (!originalValue) return

    updateGen2MaterialColor(colorName, materialName, originalValue)
    setGen2MaterialColorOptions(cloneConfigValue(gen2MaterialColorOptionsDraft.current))
  }

  const copyText = (output) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(output)
      return
    }

    const textarea = document.createElement("textarea")
    textarea.value = output
    document.body.appendChild(textarea)
    textarea.select()
    document.execCommand("copy")
    document.body.removeChild(textarea)
  }

  const copyGen2MaterialOutput = (colorName, materialName) => {
    const materials = gen2LiveMaterials.current[materialName]
    const liveMaterial = materials ? Array.from(materials)[0] : null
    const liveColor = liveMaterial?.color ? `#${liveMaterial.color.getHexString()}` : ""
    const configuredColor = gen2MaterialColorOptionsDraft.current?.[colorName]?.[materialName] || gen2MaterialColorOptions?.[colorName]?.[materialName] || ""

    copyText(liveColor || configuredColor)
  }

  const flashGen2Material = (materialName) => {
    const materials = gen2LiveMaterials.current[materialName]
    if (!materials) return

    materials.forEach((material) => {
      const previousFlash = material.userData?.devFlash
      if (previousFlash?.frame) cancelAnimationFrame(previousFlash.frame)
      if (previousFlash?.original) {
        if (previousFlash.original.color && material.color) {
          material.color.copy(previousFlash.original.color)
        }
        if (previousFlash.original.emissive && material.emissive) {
          material.emissive.copy(previousFlash.original.emissive)
          material.emissiveIntensity = previousFlash.original.emissiveIntensity
        }
        material.needsUpdate = true
      }

      const startTime = performance.now()
      const duration = 500
      const original = {
        color: material.color?.clone(),
        emissive: material.emissive?.clone(),
        emissiveIntensity: material.emissiveIntensity || 0,
      }
      const flashColor = new THREE.Color("#ffd000")
      material.userData.devFlash = { original, frame: null }

      const animateFlash = () => {
        const progress = Math.min((performance.now() - startTime) / duration, 1)
        const pulse = Math.sin(progress * Math.PI) * 0.2

        if (original.color && material.color) {
          material.color.copy(original.color).lerp(flashColor, pulse)
        }
        if (original.emissive && material.emissive) {
          material.emissive.copy(original.emissive).lerp(flashColor, pulse)
          material.emissiveIntensity = original.emissiveIntensity + pulse
        }
        material.needsUpdate = true

        if (progress < 1) {
          material.userData.devFlash.frame = requestAnimationFrame(animateFlash)
          return
        }

        if (original.color && material.color) {
          material.color.copy(original.color)
        }
        if (original.emissive && material.emissive) {
          material.emissive.copy(original.emissive)
          material.emissiveIntensity = original.emissiveIntensity
        }
        delete material.userData.devFlash
        material.needsUpdate = true
      }

      animateFlash()
    })
  }

  const updateRenderSetting = (key, value) => {
    setRenderSettings((current) => ({
      ...current,
      [key]: value,
    }))
  }

  const updateRenderNumberSetting = (key, value) => {
    const parsed = Number(value)
    if (Number.isNaN(parsed)) return
    updateRenderSetting(key, parsed)
  }

  const updateExpressiveLighting = (lightId, key, value) => {
    setExpressiveLights((current) => current.map((light) => (light.id === lightId ? { ...light, [key]: value } : light)))
  }

  const updateExpressiveLightingNumber = (lightId, key, value) => {
    const parsed = Number(value)
    if (Number.isNaN(parsed)) return
    updateExpressiveLighting(lightId, key, parsed)
  }

  const addExpressiveLight = () => {
    setExpressiveLights((current) => [...current, getInitialExpressiveLighting(current.length)])
  }

  const resetExpressiveLights = () => {
    setExpressiveLights([getInitialExpressiveLighting(0)])
  }

  const copyExpressiveLightOutput = (light) => {
    copyText(JSON.stringify(light, null, 2))
  }

  const applyGen2DevDraft = () => {
    setGen2MaterialColorOptions(cloneConfigValue(gen2MaterialColorOptionsDraft.current))
  }

  const getGen2DevOutput = () => {
    const modelEntry = cloneConfigValue(config)
    modelEntry.materialColorOptions = gen2MaterialColorOptionsDraft.current
    return `"${product}": ${JSON.stringify(modelEntry, null, 2)}`
  }

  const copyGen2DevOutput = () => {
    copyText(getGen2DevOutput())
  }

  const renderGen2DevPanel = () => {
    if (!devMode || config.generation !== 2) return null

    const materialNames = getGen2MaterialNames(gen2MaterialColorOptions)
    const currentColorName = config.colorNames[color]
    const panelStyle = {
      position: "fixed",
      top: 8,
      left: 8,
      zIndex: 50,
      width: 420,
      maxHeight: "calc(100vh - 16px)",
      overflow: "auto",
      padding: 12,
      border: "1px solid rgba(0,0,0,0.18)",
      background: "rgba(255,255,255,0.94)",
      color: "#111",
      fontFamily: "monospace",
      fontSize: 11,
      lineHeight: 1.35,
      boxShadow: "0 8px 24px rgba(0,0,0,0.14)",
    }
    const rowStyle = {
      display: "grid",
      gridTemplateColumns: "110px 34px 1fr",
      gap: 6,
      alignItems: "center",
      marginBottom: 5,
    }
    const currentMaterialRowStyle = {
      ...rowStyle,
      gridTemplateColumns: "92px 24px 34px 1fr 45px 40px",
    }
    const inputStyle = {
      width: "100%",
      boxSizing: "border-box",
      border: "1px solid #bbb",
      padding: "3px 5px",
      fontFamily: "monospace",
      fontSize: 11,
    }
    const smallButtonStyle = {
      fontSize: 10,
      padding: "3px 5px",
      border: "1px solid #bbb",
      background: "#fff",
      cursor: "pointer",
    }

    return (
      <div
        style={panelStyle}
        onPointerEnter={() => setDevEditorActive(true)}
        onPointerLeave={() => setDevEditorActive(false)}
        onFocus={() => setDevEditorActive(true)}
        onBlur={() => setDevEditorActive(false)}
        onPointerDown={(event) => event.stopPropagation()}
        onPointerUp={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
        onWheel={(event) => event.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center", marginBottom: 8 }}>
          <strong>{product} gen 2</strong>
          <div style={{ display: "flex", gap: 6 }}>
            <button type="button" onClick={applyGen2DevDraft} style={{ fontSize: 11, padding: "4px 7px" }}>
              Apply
            </button>
            <button type="button" onClick={copyGen2DevOutput} style={{ fontSize: 11, padding: "4px 7px" }}>
              Copy entry
            </button>
          </div>
        </div>

        <div style={{ marginBottom: 8 }}>
          Current color: <strong>{currentColorName}</strong>
        </div>

        <details open>
          <summary>Material Colors</summary>
          <div style={{ marginTop: 6 }}>
            {materialNames.map((materialName) => (
              <div key={materialName} style={{ marginBottom: 9, paddingBottom: 7, borderBottom: "1px solid #e2e2e2" }}>
                <div style={currentMaterialRowStyle}>
                  <span>{materialName}</span>
                  <button
                    type="button"
                    aria-label={`Flash ${materialName}`}
                    title={`Flash ${materialName}`}
                    onClick={() => flashGen2Material(materialName)}
                    style={{
                      width: 22,
                      height: 22,
                      padding: 0,
                      border: "1px solid #bbb",
                      background: "#fff",
                      cursor: "pointer",
                      position: "relative",
                    }}>
                    <span
                      aria-hidden="true"
                      style={{
                        position: "absolute",
                        left: 4,
                        top: 6,
                        width: 12,
                        height: 7,
                        border: "1px solid #111",
                        borderRadius: "50%",
                      }}
                    />
                    <span
                      aria-hidden="true"
                      style={{
                        position: "absolute",
                        left: 8,
                        top: 8,
                        width: 4,
                        height: 4,
                        background: "#111",
                        borderRadius: "50%",
                      }}
                    />
                  </button>
                  <input
                    type="color"
                    key={`${currentColorName}-${materialName}-picker-${gen2MaterialColorOptions?.[currentColorName]?.[materialName]}`}
                    defaultValue={getColorInputValue(gen2MaterialColorOptions?.[currentColorName]?.[materialName])}
                    onChange={(event) => updateGen2MaterialColor(currentColorName, materialName, event.target.value)}
                    style={{ width: 30, height: 24, padding: 0 }}
                  />
                  <input
                    key={`${currentColorName}-${materialName}-text-${gen2MaterialColorOptions?.[currentColorName]?.[materialName]}`}
                    defaultValue={gen2MaterialColorOptions?.[currentColorName]?.[materialName] || ""}
                    onChange={(event) => updateGen2MaterialColor(currentColorName, materialName, event.target.value)}
                    style={inputStyle}
                  />
                  <button type="button" onClick={() => resetGen2MaterialColor(currentColorName, materialName)} style={smallButtonStyle}>
                    Reset
                  </button>
                  <button type="button" onClick={() => copyGen2MaterialOutput(currentColorName, materialName)} style={smallButtonStyle}>
                    Copy
                  </button>
                </div>
                <div style={{ marginLeft: 116, color: "#555" }}>
                  {(devSceneObjectGroups[materialName] || []).length === 0 ? (
                    <span>No scene objects using this material.</span>
                  ) : (
                    <details open>
                      <summary>{devSceneObjectGroups[materialName].length} object part(s)</summary>
                      <div style={{ marginTop: 4 }}>
                        {devSceneObjectGroups[materialName].map((objectName) => (
                          <div key={`${materialName}-${objectName}`} style={{ padding: "2px 0" }}>
                            {objectName}
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              </div>
            ))}
          </div>
        </details>

        <details style={{ marginTop: 8 }}>
          <summary>All color options</summary>
          <div style={{ marginTop: 6 }}>
            {config.colorNames.map((colorName) => (
              <details key={colorName} style={{ marginBottom: 5 }}>
                <summary>{colorName}</summary>
                <div style={{ marginTop: 5 }}>
                  {materialNames.map((materialName) => (
                    <div key={`${colorName}-${materialName}`} style={rowStyle}>
                      <span>{materialName}</span>
                      <input
                        type="color"
                        key={`${colorName}-${materialName}-picker-${gen2MaterialColorOptions?.[colorName]?.[materialName]}`}
                        defaultValue={getColorInputValue(gen2MaterialColorOptions?.[colorName]?.[materialName])}
                        onChange={(event) => updateGen2MaterialColor(colorName, materialName, event.target.value)}
                        style={{ width: 30, height: 24, padding: 0 }}
                      />
                      <input
                        key={`${colorName}-${materialName}-text-${gen2MaterialColorOptions?.[colorName]?.[materialName]}`}
                        defaultValue={gen2MaterialColorOptions?.[colorName]?.[materialName] || ""}
                        onChange={(event) => updateGen2MaterialColor(colorName, materialName, event.target.value)}
                        style={inputStyle}
                      />
                    </div>
                  ))}
                </div>
              </details>
            ))}
          </div>
        </details>

        <details open style={{ marginTop: 8 }}>
          <summary>Live material properties</summary>
          <div style={{ marginTop: 6 }}>
            {Object.keys(gen2MaterialSnapshot).length === 0 ? (
              <div>No tracked materials found.</div>
            ) : (
              Object.entries(gen2MaterialSnapshot).map(([materialName, snapshot]) => (
                <div key={materialName} style={{ marginBottom: 8, borderTop: "1px solid #ddd", paddingTop: 5 }}>
                  <strong>{materialName}</strong>
                  <pre style={{ margin: "4px 0 0", whiteSpace: "pre-wrap" }}>{JSON.stringify(snapshot, null, 2)}</pre>
                </div>
              ))
            )}
          </div>
        </details>

        <details style={{ marginTop: 8 }}>
          <summary>Copy output preview</summary>
          <textarea readOnly value={getGen2DevOutput()} style={{ ...inputStyle, height: 180, marginTop: 6 }} />
        </details>
      </div>
    )
  }

  const renderExpressiveLightingMenu = () => {
    if (!devMode) return null

    const containerStyle = {
      position: "fixed",
      top: 8,
      left: config.generation === 2 ? 472 : 8,
      zIndex: 50,
      maxWidth: "calc(100vw - 488px)",
      width: "fit-content",
      overflow: "hidden",
      padding: 10,
      boxSizing: "border-box",
      border: "1px solid rgba(0,0,0,0.18)",
      background: "rgba(255,255,255,0.94)",
      color: "#111",
      fontFamily: "monospace",
      fontSize: 11,
      lineHeight: 1.35,
      boxShadow: "0 8px 24px rgba(0,0,0,0.14)",
    }
    const masterMenuStyle = {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 12,
      paddingBottom: 8,
      borderBottom: "1px solid #ddd",
    }
    const tabsContainerStyle = {
      display: "flex",
      gap: 8,
      alignItems: "flex-start",
      maxWidth: "calc(100vw - 508px)",
      overflowX: "auto",
      overflowY: "hidden",
      paddingTop: 8,
      paddingBottom: 2,
    }
    const menuStyle = {
      width: 280,
      padding: 10,
      flex: "0 0 280px",
      boxSizing: "border-box",
      border: "1px solid #ddd",
      background: "#fff",
    }
    const rowStyle = {
      display: "grid",
      gridTemplateColumns: "72px minmax(0, 1fr) 52px",
      gap: 6,
      alignItems: "center",
      marginTop: 6,
    }
    const inputStyle = {
      width: "100%",
      boxSizing: "border-box",
      border: "1px solid #bbb",
      padding: "3px 5px",
      fontFamily: "monospace",
      fontSize: 11,
      minWidth: 0,
    }
    const buttonStyle = {
      fontSize: 11,
      padding: "3px 6px",
      border: "1px solid #bbb",
      background: "#fff",
      cursor: "pointer",
    }
    const renderRange = (light, label, key, min, max, step) => (
      <label style={rowStyle}>
        <span>{label}</span>
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={light[key]}
          onChange={(event) => updateExpressiveLightingNumber(light.id, key, event.target.value)}
          style={{ minWidth: 0, width: "100%" }}
        />
        <input
          type="number"
          min={min}
          max={max}
          step={step}
          value={light[key]}
          onChange={(event) => updateExpressiveLightingNumber(light.id, key, event.target.value)}
          style={inputStyle}
        />
      </label>
    )

    return (
      <div
        style={containerStyle}
        onPointerEnter={() => setDevEditorActive(true)}
        onPointerLeave={() => setDevEditorActive(false)}
        onFocus={() => setDevEditorActive(true)}
        onBlur={() => setDevEditorActive(false)}
        onPointerDown={(event) => event.stopPropagation()}
        onPointerUp={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
        onWheel={(event) => event.stopPropagation()}>
        <div style={masterMenuStyle}>
          <strong>Expressive lighting</strong>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flex: "0 0 auto" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input type="checkbox" checked={showExpressiveLights} onChange={(event) => setShowExpressiveLights(event.target.checked)} />
              show light
            </label>
            <button type="button" onClick={resetExpressiveLights} style={buttonStyle}>
              Reset
            </button>
            <button type="button" onClick={() => setExpressiveLightingMinimized((current) => !current)} style={buttonStyle}>
              {expressiveLightingMinimized ? "Expand" : "Minimize"}
            </button>
          </div>
        </div>
        {!expressiveLightingMinimized && <div style={tabsContainerStyle}>
          {expressiveLights.map((light, index) => (
            <div key={light.id} style={menuStyle}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 6, alignItems: "center", minWidth: 0 }}>
                <strong>{light.name || `Light ${index + 1}`}</strong>
                <div style={{ display: "flex", gap: 4, flex: "0 0 auto" }}>
                  <button type="button" onClick={() => copyExpressiveLightOutput(light)} style={buttonStyle}>
                    Copy
                  </button>
                  {index === expressiveLights.length - 1 && (
                    <button type="button" onClick={addExpressiveLight} style={buttonStyle}>
                      Add
                    </button>
                  )}
                </div>
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8 }}>
                <input type="checkbox" checked={light.enabled} onChange={(event) => updateExpressiveLighting(light.id, "enabled", event.target.checked)} />
                enabled
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6 }}>
                <input
                  type="checkbox"
                  checked={Boolean(light.pinToCamera)}
                  onChange={(event) => updateExpressiveLighting(light.id, "pinToCamera", event.target.checked)}
                />
                pin to camera
              </label>
              <label style={{ display: "grid", gridTemplateColumns: "82px 1fr", gap: 6, alignItems: "center", marginTop: 6 }}>
                <span>type</span>
                <select value={light.type} onChange={(event) => updateExpressiveLighting(light.id, "type", event.target.value)} style={inputStyle}>
                  <option value="spot">spot</option>
                  <option value="point">point</option>
                  <option value="area">area</option>
                </select>
              </label>
              <label style={{ display: "grid", gridTemplateColumns: "82px 1fr", gap: 6, alignItems: "center", marginTop: 6 }}>
                <span>color</span>
                <input
                  type="color"
                  value={getColorInputValue(light.color)}
                  onChange={(event) => updateExpressiveLighting(light.id, "color", event.target.value)}
                  style={{ width: "100%", height: 24, padding: 0 }}
                />
              </label>
              {renderRange(light, "rotate", "rotate", 0, 360, 1)}
              {renderRange(light, "width", "width", 0, 30, 0.1)}
              {renderRange(light, "y-position", "y", -10, 20, 0.1)}
              {renderRange(light, "intensity", "intensity", 0, 30, 0.1)}
              {light.type === "spot" && renderRange(light, "angle", "angle", 0.05, 1.4, 0.01)}
              {light.type === "spot" && renderRange(light, "penumbra", "penumbra", 0, 1, 0.01)}
              {light.type === "area" && renderRange(light, "area w", "areaWidth", 0.1, 20, 0.1)}
              {light.type === "area" && renderRange(light, "area h", "areaHeight", 0.1, 20, 0.1)}
            </div>
          ))}
        </div>}
      </div>
    )
  }

  const renderGen2ModelMenu = () => {
    if (!devMode) return null

    const gen2Models = Object.entries(ModelConfig)
      .filter(([, modelConfig]) => modelConfig.generation === 2 && !modelConfig.disabled)
      .map(([modelName]) => modelName)
    const menuStyle = {
      position: "fixed",
      top: 8,
      right: 8,
      zIndex: 50,
      width: 180,
      padding: 10,
      border: "1px solid rgba(0,0,0,0.18)",
      background: "rgba(255,255,255,0.94)",
      color: "#111",
      fontFamily: "monospace",
      fontSize: 11,
      lineHeight: 1.35,
      boxShadow: "0 8px 24px rgba(0,0,0,0.14)",
    }
    const linkStyle = {
      display: "block",
      padding: "5px 6px",
      marginTop: 4,
      color: "#111",
      textDecoration: "none",
      border: "1px solid transparent",
    }

    return (
      <div
        style={menuStyle}
        onPointerEnter={() => setDevEditorActive(true)}
        onPointerLeave={() => setDevEditorActive(false)}
        onFocus={() => setDevEditorActive(true)}
        onBlur={() => setDevEditorActive(false)}
        onPointerDown={(event) => event.stopPropagation()}
        onPointerUp={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
        onWheel={(event) => event.stopPropagation()}>
        <strong>Gen 2 models</strong>
        <div style={{ marginTop: 6 }}>
          {gen2Models.map((modelName) => {
            const params = new URLSearchParams(window.location.search)
            params.set("product", modelName)
            params.set("dev", "true")

            return (
              <a
                key={modelName}
                href={`${window.location.pathname}?${params.toString()}`}
                style={{
                  ...linkStyle,
                  background: product === modelName ? "#111" : "transparent",
                  color: product === modelName ? "#fff" : "#111",
                  borderColor: product === modelName ? "#111" : "#ddd",
                }}>
                {modelName}
              </a>
            )
          })}
        </div>
      </div>
    )
  }

  const renderRenderSettingsMenu = () => {
    if (!devMode) return null

    const menuStyle = {
      position: "fixed",
      top: 170,
      right: 8,
      zIndex: 50,
      width: 260,
      maxHeight: "calc(100vh - 186px)",
      overflow: "auto",
      padding: 10,
      border: "1px solid rgba(0,0,0,0.18)",
      background: "rgba(255,255,255,0.94)",
      color: "#111",
      fontFamily: "monospace",
      fontSize: 11,
      lineHeight: 1.35,
      boxShadow: "0 8px 24px rgba(0,0,0,0.14)",
    }
    const rowStyle = {
      display: "grid",
      gridTemplateColumns: "112px 1fr",
      gap: 8,
      alignItems: "center",
      marginTop: 6,
    }
    const inputStyle = {
      width: "100%",
      boxSizing: "border-box",
      border: "1px solid #bbb",
      padding: "3px 5px",
      fontFamily: "monospace",
      fontSize: 11,
    }
    const sectionStyle = {
      marginTop: 8,
      paddingTop: 8,
      borderTop: "1px solid #ddd",
    }
    const renderToggle = (label, key) => (
      <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6 }}>
        <input type="checkbox" checked={Boolean(renderSettings[key])} onChange={(event) => updateRenderSetting(key, event.target.checked)} />
        {label}
      </label>
    )
    const renderNumber = (label, key, step = 0.1, min = 0) => (
      <label style={rowStyle}>
        <span>{label}</span>
        <input
          type="number"
          value={renderSettings[key]}
          min={min}
          step={step}
          onChange={(event) => updateRenderNumberSetting(key, event.target.value)}
          style={inputStyle}
        />
      </label>
    )
    const hdriOptions = Array.from(new Set(Object.values(ModelConfig).map((modelConfig) => modelConfig.hdri).filter(Boolean))).sort()

    return (
      <div
        style={menuStyle}
        onPointerEnter={() => setDevEditorActive(true)}
        onPointerLeave={() => setDevEditorActive(false)}
        onFocus={() => setDevEditorActive(true)}
        onBlur={() => setDevEditorActive(false)}
        onPointerDown={(event) => event.stopPropagation()}
        onPointerUp={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
        onWheel={(event) => event.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
          <strong>Render settings</strong>
          <button type="button" onClick={() => setRenderSettings(getInitialRenderSettings(config, color))} style={{ fontSize: 11, padding: "3px 6px" }}>
            Reset
          </button>
        </div>

        <div style={sectionStyle}>
          <strong>Canvas</strong>
          <label style={rowStyle}>
            <span>background</span>
            <input
              type="color"
              value={getColorInputValue(renderSettings.backgroundColor)}
              onChange={(event) => updateRenderSetting("backgroundColor", event.target.value)}
              style={{ width: "100%", height: 24, padding: 0 }}
            />
          </label>
          {renderToggle("antialias", "antialias")}
          {renderToggle("alpha", "alpha")}
          {renderToggle("flat", "flat")}
          {renderNumber("desktop dpr", "dprDesktop", 0.1, 0.1)}
          {renderNumber("mobile dpr", "dprMobile", 0.1, 0.1)}
        </div>

        <div style={sectionStyle}>
          <strong>Lighting</strong>
          {renderToggle("environment", "environment")}
          {renderToggle("shadow", "shadow")}
          <label style={rowStyle}>
            <span>hdri</span>
            <select value={renderSettings.hdri} onChange={(event) => updateRenderSetting("hdri", event.target.value)} style={inputStyle}>
              {hdriOptions.map((hdri) => (
                <option key={hdri} value={hdri}>
                  {hdri}
                </option>
              ))}
            </select>
          </label>
          {renderNumber("envmap", "envmap", 0.05, 0)}
          {renderNumber("ambient", "ambient", 0.05, 0)}
        </div>

        <div style={sectionStyle}>
          <strong>Post processing</strong>
          {renderToggle("enabled", "postProcessing")}
          {renderToggle("n8ao", "n8ao")}
          {renderNumber("ssao strength", "ssaoIntensity", 0.05, 0)}
          {renderNumber("ao radius", "aoRadius", 0.05, 0)}
          {renderNumber("ao samples", "aoSamples", 1, 1)}
          {renderNumber("denoise", "denoiseSamples", 1, 0)}
          {renderToggle("tone mapping", "toneMapping")}
          {renderNumber("tone res", "toneResolution", 1, 1)}
          {renderNumber("max luminance", "toneMaxLuminance", 0.1, 0)}
          {renderNumber("white point", "toneWhitePoint", 0.1, 0)}
          {renderNumber("middle grey", "toneMiddleGrey", 0.01, 0)}
          {renderNumber("avg luminance", "toneAverageLuminance", 0.1, 0)}
          {renderToggle("bloom", "bloom")}
          {renderNumber("bloom intensity", "bloomIntensity", 0.05, 0)}
          {renderNumber("bloom threshold", "bloomLuminanceThreshold", 0.01, 0)}
          {renderNumber("bloom smoothing", "bloomLuminanceSmoothing", 0.01, 0)}
          {renderToggle("noise", "noise")}
          {renderNumber("noise opacity", "noiseOpacity", 0.01, 0)}
          {renderToggle("vignette", "vignette")}
          {renderNumber("vignette offset", "vignetteOffset", 0.01, 0)}
          {renderNumber("vignette dark", "vignetteDarkness", 0.01, 0)}
        </div>
      </div>
    )
  }

  return (
    <>
      <div className={"container"} style={{ fontFamily: getFontFamily(localeLang) }}>
        {renderGen2DevPanel()}
        {renderExpressiveLightingMenu()}
        {renderGen2ModelMenu()}
        {renderRenderSettingsMenu()}
        {renderHandOverlay()}
        <div
          style={{ display: closeButton == "true" ? "block" : "none" }}
          aria-label="Close Demo"
          className={"closeButton"}
          onClick={() => {
            if (window !== window.parent) {
              window.parent.postMessage("360_close", "*")
            } else {
              window.location.href =
                "http://www.bose.com/en_us/products/speakers/home_theater/bose-smart-soundbar-900.html#v=bose_smart_soundbar_900_white"
            }
          }}>
          <img className={"closeIcon"} src="/close.svg" />
        </div>
        <div className={"colorPicker"} style={{ bottom: reseller ? "30px" : "5px" }}>
          <h3>
            {`${t("general.color")}:`} <strong>{t(`${product}.colors.${color}`)}</strong>
          </h3>
          <div className={"colorOptions"}>{colorList}</div>
        </div>
        {/* <PrivacyOverlay /> */}
        <LanguageOverlay />
        {/* <InView
          style={{
            display: "flex",
            width: "100%",
            height: "100%",
            justifyContent: "center",
          }}
          onChange={setInView}
        > */}
        <Canvas
          key={`${renderSettings.antialias}-${renderSettings.alpha}`}
          ref={canvas}
          gl={{ antialias: renderSettings.antialias, alpha: renderSettings.alpha }}
          onCreated={(state) => {
            state.gl.setClearColor(renderSettings.backgroundColor)
          }}
          dpr={getDpr()}
          flat={renderSettings.flat}
          camera={{ fov: 60, position: isMobile ? config.cameraPositionMobile : config.cameraPositionDesktop }}>
          <CanvasSettingsApplier backgroundColor={renderSettings.backgroundColor} />
          <CameraControls />
          <ambientLight intensity={renderSettings.ambient} />
          {expressiveLights
            .filter((light) => light.enabled)
            .map((light) => (
              <ExpressiveLight key={light.id} settings={light} targetPosition={config.position} />
            ))}
          {showExpressiveLights &&
            expressiveLights
              .filter((light) => light.enabled)
              .map((light) => <ExpressiveLightHelper key={`helper-${light.id}`} settings={light} targetPosition={config.position} />)}

          <Suspense fallback={<Loader />}>
            <LoadedModel />
            <Shadow />

            {renderSettings.environment && <Environment files={renderSettings.hdri || config.hdri} environmentRotation={(config.hdriRotation || [0, 0, 0]).map((angle) => THREE.MathUtils.degToRad(angle))} />}

            {renderSettings.postProcessing && (
              <EffectComposer disableNormalPass autoClear={false}>
                {renderSettings.n8ao && (
                  <N8AO
                    halfRes
                    color="black"
                    ada
                    aoRadius={renderSettings.aoRadius}
                    intensity={renderSettings.ssaoIntensity}
                    aoSamples={renderSettings.aoSamples}
                    denoiseSamples={renderSettings.denoiseSamples}
                  />
                )}
                {renderSettings.toneMapping && (
                  <ToneMapping
                    adaptive={false}
                    resolution={renderSettings.toneResolution}
                    maxLuminance={renderSettings.toneMaxLuminance}
                    whitePoint={renderSettings.toneWhitePoint}
                    middleGrey={renderSettings.toneMiddleGrey}
                    mode={ToneMappingMode.ACES_FILMIC}
                    minLuminance={0.01}
                    averageLuminance={renderSettings.toneAverageLuminance}
                    adaptationRate={0}
                  />
                )}
                {renderSettings.bloom && (
                  <Bloom
                    intensity={renderSettings.bloomIntensity}
                    luminanceThreshold={renderSettings.bloomLuminanceThreshold}
                    luminanceSmoothing={renderSettings.bloomLuminanceSmoothing}
                  />
                )}
                {renderSettings.noise && <Noise opacity={renderSettings.noiseOpacity} />}
                {renderSettings.vignette && <Vignette offset={renderSettings.vignetteOffset} darkness={renderSettings.vignetteDarkness} />}
              </EffectComposer>
            )}
          </Suspense>
        </Canvas>
        {/* </InView> */}
        <div style={{ position: "relative", width: "100%" }}>
          <ResellerBar />
        </div>
      </div>
    </>
  )
}

export default function App() {
  const product = new URLSearchParams(window.location.search).get("product")

  if (ModelConfig[product]?.disabled) return null

  return <Viewer product={product} />
}
